import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { format, addDays } from "date-fns";
import {
  CalendarDays, RefreshCw, Trash2, Plus, Loader2, Link2, AlertCircle, Check, CreditCard,
  Star, GripVertical, ArrowUp, ArrowDown, Image as ImageIcon, Pencil, X, Send, Copy, Tag, Clock, Mail, CalendarRange, BellRing, Sunrise, Sunset, Minus, ExternalLink, Eye, EyeOff, MessageSquare, Users, DollarSign, ChevronDown, Download, CheckCircle2, FileText, IdCard, Upload,
} from "lucide-react";
import api, { API, formatApiErrorDetail, money } from "@/lib/api";
import { AmenityIcon } from "@/lib/icons";
import { EVENT_ICONS, EVENT_ICON_NAMES } from "@/lib/events";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const Card = ({ children, className = "", ...props }) => (
  <div {...props} className={`bg-card rounded-xl border border-border p-6 ${className}`}>{children}</div>
);
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));
const iso = (d) => format(d, "yyyy-MM-dd");
const BOOKED_RGB = {
  airbnb: "239,68,68",
  vrbo: "59,130,246",
  booking: "37,99,235",
  other: "34,197,94",
  direct: "100,116,139",
  manual: "100,116,139",
};
const stripeStyle = (src) => {
  const c = BOOKED_RGB[src] || BOOKED_RGB.manual;
  return { backgroundImage: `repeating-linear-gradient(45deg, rgba(${c},0.32) 0, rgba(${c},0.32) 1.5px, transparent 1.5px, transparent 7px)` };
};

const STATUS_COLORS = {
  confirmed: "bg-secondary/20 text-secondary",
  pending_payment: "bg-amber-100 text-amber-700",
  awaiting_approval: "bg-orange-100 text-orange-700",
  cancelled: "bg-muted text-muted-foreground",
};

const AMENITY_ICONS = [
  "wifi", "flame", "utensils", "car", "waves", "tv", "baby", "trees", "washing-machine",
  "wind", "coffee", "dog", "home", "bath", "bed", "users", "snowflake", "parking", "sparkles",
];

/* ---------------- Overview ---------------- */
export function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(err); }, []);
  if (!stats) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const cards = [
    { label: "Total bookings", value: stats.total_bookings },
    { label: "Confirmed", value: stats.confirmed },
    { label: "Pending payment", value: stats.pending },
    { label: "New inquiries", value: stats.new_inquiries },
    { label: "Deposits collected", value: money(stats.deposits_collected) },
    { label: "Booked value", value: money(stats.booked_value) },
  ];
  const snapshot = [
    { label: `Occupancy — ${stats.month_label}`, value: `${stats.occupancy_rate}%`, sub: `${stats.nights_booked_this_month} of ${stats.days_in_month} nights booked`, testid: "snapshot-occupancy" },
    { label: "Upcoming check-ins", value: stats.upcoming_checkins, sub: "next 30 days", testid: "snapshot-upcoming" },
    { label: "Balance collected", value: money(stats.balance_collected), sub: `${money(stats.balance_outstanding)} still outstanding`, testid: "snapshot-balance" },
    { label: "Add-on revenue", value: money(stats.addon_revenue), sub: `${stats.addon_count} upsells paid`, testid: "snapshot-addon-revenue" },
  ];
  return (
    <div>
      <h1 className="font-serif text-3xl font-semibold text-foreground mb-6">Overview</h1>

      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-widest mb-3">Occupancy & revenue</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {snapshot.map((c) => (
          <Card key={c.label} data-testid={c.testid} className="bg-primary/5 border-primary/10">
            <div className="text-sm text-muted-foreground">{c.label}</div>
            <div className="font-serif text-3xl font-semibold text-primary mt-2">{c.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{c.sub}</div>
          </Card>
        ))}
      </div>

      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-widest mb-3">All-time</h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <Card key={c.label} data-testid={`stat-${c.label}`}>
            <div className="text-sm text-muted-foreground">{c.label}</div>
            <div className="font-serif text-3xl font-semibold text-foreground mt-2">{c.value}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Bookings ---------------- */
export function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [hideCancelled, setHideCancelled] = useState(false);
  const [balDialog, setBalDialog] = useState(null); // { url, amount, emailed }
  const [busyId, setBusyId] = useState(null);
  const load = () => api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(err);
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/bookings/${id}`, { status }); toast.success("Updated"); load(); } catch (e) { err(e); }
  };

  const collectBalance = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/admin/bookings/${id}/balance-link`, { origin_url: window.location.origin });
      setBalDialog(data);
      toast.success(data.emailed ? "Payment link emailed to guest" : "Payment link created");
    } catch (e) { err(e); } finally { setBusyId(null); }
  };

  const refundDeposit = async (id, refunded) => {
    try { await api.patch(`/admin/bookings/${id}/refund-deposit`, { refunded }); toast.success(refunded ? "Deposit marked refunded" : "Marked as not refunded"); load(); } catch (e) { err(e); }
  };

  const decide = async (id, decision) => {
    setBusyId(id);
    try { await api.patch(`/admin/bookings/${id}/approval`, { decision }); toast.success(decision === "approve" ? "Booking approved & confirmed" : "Booking declined — remember to refund the deposit in Stripe"); load(); } catch (e) { err(e); } finally { setBusyId(null); }
  };

  const [msgBooking, setMsgBooking] = useState(null); // booking whose message log is open
  const [resending, setResending] = useState(null);
  const [idDialog, setIdDialog] = useState(null); // { url, isPdf, name }
  const [idLoading, setIdLoading] = useState(null);
  const viewId = async (b) => {
    setIdLoading(b.id);
    try {
      const { data } = await api.get(`/admin/bookings/${b.id}/id-document`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      setIdDialog({ url, isPdf: (data.type || "").includes("pdf"), name: b.guest_name });
    } catch (e) { err(e); } finally { setIdLoading(null); }
  };
  const MSG_LABELS = { pre_arrival: "Pre-arrival note", check_in_day: "Check-in day welcome", balance_reminder: "Balance-due reminder", post_checkout: "Post-checkout thank-you" };
  const resendMsg = async (bookingId, type) => {
    setResending(type);
    try {
      const { data } = await api.post(`/admin/bookings/${bookingId}/resend-message`, { type });
      toast.success(data.entry?.emailed ? "Message sent" : "Sent (email skipped — no key or test-mode)");
      const { data: fresh } = await api.get("/admin/bookings");
      setMsgBooking(fresh.find((x) => x.id === bookingId) || null);
      setBookings(fresh);
    } catch (e) { err(e); } finally { setResending(null); }
  };

  const cancelledCount = bookings.filter((b) => b.status === "cancelled").length;
  const visibleBookings = hideCancelled ? bookings.filter((b) => b.status !== "cancelled") : bookings;

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <h1 className="font-serif text-3xl font-semibold text-foreground">Bookings</h1>
        <Button
          variant="outline"
          className="rounded-full"
          data-testid="toggle-cancelled-btn"
          onClick={() => setHideCancelled((v) => !v)}
        >
          {hideCancelled ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
          {hideCancelled ? `Show cancelled${cancelledCount ? ` (${cancelledCount})` : ""}` : "Hide cancelled"}
        </Button>
      </div>
      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Booking #</TableHead><TableHead>Guest</TableHead><TableHead>Dates</TableHead><TableHead>Guests</TableHead>
              <TableHead>Total</TableHead><TableHead>Balance</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleBookings.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">{bookings.length === 0 ? "No bookings yet" : "No bookings to show"}</TableCell></TableRow>}
            {visibleBookings.map((b) => (
              <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                <TableCell>
                  <span className="font-mono font-semibold text-foreground" data-testid={`booking-number-${b.id}`}>{b.booking_number || "—"}</span>
                </TableCell>
                <TableCell>
                  <div className="font-medium text-foreground">{b.guest_name}</div>
                  <div className="text-xs text-muted-foreground">{b.guest_email}</div>
                  <div className="text-xs text-muted-foreground" data-testid={`booking-phone-${b.id}`}>{b.guest_phone || "No phone provided"}</div>
                  {b.waiver_info && (
                    <details className="mt-1.5" data-testid={`waiver-info-${b.id}`}>
                      <summary className="text-[11px] text-primary cursor-pointer select-none">Damage waiver enrollment</summary>
                      <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed bg-accent/40 rounded-md p-2">
                        <div className="text-foreground font-medium">{b.waiver_info.first_name} {b.waiver_info.last_name}</div>
                        <div>{b.waiver_info.address1}{b.waiver_info.unit ? `, Unit ${b.waiver_info.unit}` : ""}</div>
                        <div>{b.waiver_info.city}, {b.waiver_info.state} {b.waiver_info.postal_code}</div>
                        <div>{b.waiver_info.phone}</div>
                        <div>{b.waiver_info.email}</div>
                      </div>
                    </details>
                  )}
                  {b.agreement_accepted && (
                    <div className="text-[11px] text-secondary mt-1 flex items-center gap-1" data-testid={`agreement-signed-${b.id}`} title={b.agreement_accepted_at ? `Signed ${new Date(b.agreement_accepted_at).toLocaleString()}` : "Rental agreement signed"}>
                      <CheckCircle2 className="h-3 w-3" /> Agreement signed{b.agreement_signature ? ` — ${b.agreement_signature}` : ""}
                    </div>
                  )}
                  {b.id_document_path && (
                    <button type="button" className="text-[11px] text-primary underline mt-1 flex items-center gap-1 disabled:opacity-50" data-testid={`view-id-${b.id}`} disabled={idLoading === b.id} onClick={() => viewId(b)}>
                      {idLoading === b.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <IdCard className="h-3 w-3" />} View ID
                    </button>
                  )}
                </TableCell>
                <TableCell className="text-sm">{b.check_in} → {b.check_out}</TableCell>
                <TableCell>{b.guests}</TableCell>
                <TableCell>
                  {money(b.total, b.currency)}
                  {b.damage_option === "deposit" && b.security_deposit_amount > 0 && (
                    <div className="text-[11px] text-secondary mt-0.5" data-testid={`booking-deposit-${b.id}`}>incl. {money(b.security_deposit_amount, b.currency)} refundable deposit</div>
                  )}
                  {b.damage_option === "waiver" && b.damage_waiver_fee > 0 && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">incl. {money(b.damage_waiver_fee, b.currency)} damage waiver</div>
                  )}
                </TableCell>
                <TableCell>
                  {b.balance_paid ? <Badge className="bg-secondary/20 text-secondary border-0">Paid</Badge> : <span className="text-foreground">{money(b.balance_due, b.currency)}</span>}
                </TableCell>
                <TableCell><Badge className={`${STATUS_COLORS[b.status]} border-0`}>{b.status.replace("_", " ")}</Badge></TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                      <SelectTrigger className="w-32 h-8 text-xs" data-testid={`booking-status-${b.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending_payment">Pending</SelectItem>
                        <SelectItem value="awaiting_approval">Awaiting approval</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    {b.status === "awaiting_approval" && (
                      <>
                        <Button size="sm" className="h-8 text-xs whitespace-nowrap bg-secondary hover:bg-secondary/90" disabled={busyId === b.id} data-testid={`approve-${b.id}`} onClick={() => decide(b.id, "approve")}>
                          {busyId === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5 mr-1" /> Approve</>}
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 text-xs whitespace-nowrap text-destructive border-destructive/40" disabled={busyId === b.id} data-testid={`decline-${b.id}`} onClick={() => decide(b.id, "decline")}>
                          Decline
                        </Button>
                      </>
                    )}
                    {b.status === "confirmed" && !b.balance_paid && b.balance_due > 0 && (
                      <Button size="sm" variant="outline" className="h-8 text-xs whitespace-nowrap" disabled={busyId === b.id} data-testid={`collect-balance-${b.id}`} onClick={() => collectBalance(b.id)}>
                        {busyId === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CreditCard className="h-3.5 w-3.5 mr-1" /> Collect balance</>}
                      </Button>
                    )}
                    {b.damage_option === "deposit" && b.security_deposit_amount > 0 && (
                      b.security_deposit_refunded ? (
                        <Badge className="bg-secondary/20 text-secondary border-0 cursor-pointer" data-testid={`deposit-refunded-${b.id}`} onClick={() => refundDeposit(b.id, false)} title="Click to undo">
                          <Check className="h-3 w-3 mr-1" /> Deposit refunded
                        </Badge>
                      ) : (
                        <Button size="sm" variant="outline" className="h-8 text-xs whitespace-nowrap" data-testid={`refund-deposit-${b.id}`} onClick={() => refundDeposit(b.id, true)}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Mark deposit refunded
                        </Button>
                      )
                    )}
                    <Button size="sm" variant="ghost" className="h-8 text-xs whitespace-nowrap text-muted-foreground" data-testid={`messages-${b.id}`} onClick={() => setMsgBooking(b)}>
                      <Mail className="h-3.5 w-3.5 mr-1" /> Messages{(b.message_log || []).length ? ` (${b.message_log.length})` : ""}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!idDialog} onOpenChange={() => { if (idDialog?.url) URL.revokeObjectURL(idDialog.url); setIdDialog(null); }}>
        <DialogContent className="sm:max-w-2xl" data-testid="id-view-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Government ID — {idDialog?.name}</DialogTitle>
            <DialogDescription>Uploaded by the guest at booking for verification and insurance.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-auto">
            {idDialog?.isPdf ? (
              <iframe title="Guest ID" src={idDialog.url} className="w-full h-[65vh] rounded-lg border border-border" data-testid="id-view-pdf" />
            ) : (
              <img src={idDialog?.url} alt="Guest government ID" className="w-full rounded-lg border border-border" data-testid="id-view-image" />
            )}
          </div>
          {idDialog?.url && (
            <a href={idDialog.url} download={`guest-id-${(idDialog.name || "guest").replace(/\s+/g, "-")}`} className="text-sm text-primary underline" data-testid="id-download-link">Download</a>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!msgBooking} onOpenChange={() => setMsgBooking(null)}>
        <DialogContent data-testid="messages-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Guest messages — {msgBooking?.guest_name}</DialogTitle></DialogHeader>
          <div className="space-y-2 py-1">
            {["pre_arrival", "check_in_day", "balance_reminder", "post_checkout"].map((type) => {
              const entries = (msgBooking?.message_log || []).filter((e) => e.type === type);
              const last = entries[entries.length - 1];
              return (
                <div key={type} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2" data-testid={`msg-row-${type}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">{MSG_LABELS[type]}</div>
                    {last ? (
                      <div className="text-xs text-muted-foreground">Sent {new Date(last.sent_at).toLocaleString()}{last.emailed ? "" : " (email skipped)"}</div>
                    ) : (
                      <div className="text-xs text-muted-foreground">Not sent yet</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {last && <Badge className="bg-secondary/20 text-secondary border-0"><Check className="h-3 w-3 mr-1" /> Sent</Badge>}
                    <Button size="sm" variant="outline" className="h-8 text-xs" disabled={resending === type} data-testid={`resend-${type}`} onClick={() => resendMsg(msgBooking.id, type)}>
                      {resending === type ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (last ? "Resend" : "Send now")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">These normally send automatically on schedule. Use "Send now"/"Resend" to trigger one manually. Email delivery requires RESEND_API_KEY.</p>
        </DialogContent>
      </Dialog>

      <Dialog open={!!balDialog} onOpenChange={() => setBalDialog(null)}>
        <DialogContent data-testid="balance-link-dialog" aria-describedby="balance-dialog-desc">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Balance payment link</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground" data-testid="balance-dialog-desc">
            A secure balance-payment page link for <strong className="text-foreground">{money(balDialog?.amount)}</strong> has been {balDialog?.emailed ? "emailed to the guest" : "created"}. You can also copy and share it directly:
          </p>
          <div className="flex gap-2">
            <Input readOnly value={balDialog?.url || ""} className="rounded-lg text-xs" data-testid="balance-link-url" onFocus={(e) => e.target.select()} />
            <Button variant="secondary" className="rounded-lg" data-testid="balance-link-copy" onClick={() => { navigator.clipboard?.writeText(balDialog?.url || ""); toast.success("Copied"); }}>Copy</Button>
          </div>
          {!balDialog?.emailed && <p className="text-xs text-muted-foreground">Email wasn't sent (no email key configured) — share the link manually.</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Calendar (blocks + sync) ---------------- */
const timeAgo = (iso) => {
  if (!iso) return null;
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60); if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60); if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24); return `${days}d ago`;
};
const hoursSince = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / 3600000 : Infinity);

function PricingCalendar() {
  const [mode, setMode] = useState("range");
  const [selected, setSelected] = useState(undefined);
  const [priceMap, setPriceMap] = useState({});
  const [overrides, setOverrides] = useState({});
  const [bookedMap, setBookedMap] = useState({});
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);

  const switchMode = (m) => { setMode(m); setSelected(m === "range" ? undefined : []); };

  const fetchPrices = (fromDate) => {
    api.get(`/availability/prices?start=${iso(fromDate)}&end=${iso(addDays(fromDate, 120))}`)
      .then((r) => setPriceMap((p) => ({ ...p, ...r.data }))).catch(() => {});
  };
  const loadOverrides = () => api.get("/admin/date-prices").then((r) => setOverrides(r.data || {})).catch(err);
  const loadBooked = () => api.get("/availability").then((r) => {
    const map = {};
    (r.data.blocked || []).forEach((b) => {
      let d = new Date(b.start + "T00:00:00");
      const end = new Date(b.end + "T00:00:00");
      while (d < end) { map[iso(d)] = b.source || "manual"; d = addDays(d, 1); }
    });
    setBookedMap(map);
  }).catch(() => {});
  useEffect(() => { fetchPrices(new Date()); loadOverrides(); loadBooked(); }, []);

  const selectedDates = mode === "range"
    ? (selected?.from ? (() => { const out = []; let d = selected.from; const to = selected.to || selected.from; while (d <= to) { out.push(d); d = addDays(d, 1); } return out; })() : [])
    : (selected || []);
  const selectedIso = selectedDates.map(iso);
  const count = selectedDates.length;
  const clearSelection = () => setSelected(mode === "range" ? undefined : []);

  const apply = async () => {
    const n = Number(price);
    if (!count) { toast.error("Select one or more dates first"); return; }
    if (!n || n <= 0) { toast.error("Enter a nightly price"); return; }
    setBusy(true);
    try {
      await api.post("/admin/date-prices", { dates: selectedIso, nightly_rate: n });
      toast.success(`Price set for ${count} date(s)`);
      setPriceMap((p) => { const c = { ...p }; selectedIso.forEach((d) => { c[d] = Math.round(n); }); return c; });
      clearSelection(); setPrice(""); loadOverrides();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!count) { toast.error("Select the dates to reset"); return; }
    setBusy(true);
    try {
      await api.post("/admin/date-prices/clear", { dates: selectedIso });
      toast.success(`Reset ${count} date(s) to default`);
      clearSelection(); loadOverrides();
      const { data } = await api.get(`/availability/prices?start=${iso(new Date())}&end=${iso(addDays(new Date(), 120))}`);
      setPriceMap((p) => ({ ...p, ...data }));
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const renderDay = ({ date, activeModifiers }) => {
    const key = iso(date);
    const p = priceMap[key];
    const custom = key in overrides;
    const outside = activeModifiers?.outside;
    const src = outside ? null : bookedMap[key];
    return (
      <div className="relative flex flex-col items-center justify-center leading-none w-full h-full">
        {src ? <span className="absolute inset-0 rounded-md pointer-events-none" style={stripeStyle(src)} aria-label={`Booked (${src})`} /> : null}
        <span className="relative z-10">{date.getDate()}</span>
        {p && !outside ? <span className={`relative z-10 text-[11px] mt-0.5 tabular-nums ${custom ? "text-primary font-semibold" : "opacity-70 font-medium"}`}>${p}</span> : null}
      </div>
    );
  };

  return (
    <Card data-testid="custom-pricing-card">
      <div className="flex items-center gap-2 mb-1"><CalendarRange className="h-5 w-5 text-primary" /><h2 className="font-semibold text-foreground">Custom nightly pricing</h2></div>
      <p className="text-sm text-muted-foreground mb-4">Pick a <span className="font-medium text-foreground">date range</span> to price a whole block at once, or switch to <span className="font-medium text-foreground">individual dates</span> to hand-pick days. Enter a nightly price, then apply it to everything selected. Custom-priced dates appear in <span className="text-primary font-medium">color</span>.</p>
      <div className="flex gap-1 mb-4 p-1 rounded-full bg-muted w-fit" data-testid="pricing-mode-toggle">
        <button type="button" onClick={() => switchMode("range")} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${mode === "range" ? "bg-background shadow text-foreground" : "text-muted-foreground"}`} data-testid="pricing-mode-range">Date range</button>
        <button type="button" onClick={() => switchMode("multiple")} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${mode === "multiple" ? "bg-background shadow text-foreground" : "text-muted-foreground"}`} data-testid="pricing-mode-multiple">Individual dates</button>
      </div>
      <div className="flex flex-wrap gap-3 mb-5" data-testid="platform-calendar-links">
        <a
          href="https://www.airbnb.com/multicalendar/578345506807055463/year/2026"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="airbnb-calendar-link"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5"
          style={{ backgroundColor: "#FF5A5F" }}
        >
          <ExternalLink className="h-4 w-4" /> Airbnb calendar
        </a>
        <a
          href="https://www.vrbo.com/p/calendar/321.2660023.3230699"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="vrbo-calendar-link"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5"
          style={{ backgroundColor: "#1F5FCC" }}
        >
          <ExternalLink className="h-4 w-4" /> VRBO calendar
        </a>
      </div>
      <div className="flex flex-wrap gap-4 mb-4 text-xs text-muted-foreground" data-testid="booked-legend">
        <span className="flex items-center gap-1.5"><span className="h-4 w-6 rounded border border-border" style={stripeStyle("airbnb")} /> Airbnb</span>
        <span className="flex items-center gap-1.5"><span className="h-4 w-6 rounded border border-border" style={stripeStyle("vrbo")} /> VRBO</span>
        <span className="flex items-center gap-1.5"><span className="h-4 w-6 rounded border border-border" style={stripeStyle("other")} /> Host share</span>
        <span className="flex items-center gap-1.5"><span className="h-4 w-6 rounded border border-border" style={stripeStyle("direct")} /> Direct / manual</span>
      </div>
      <div className="grid lg:grid-cols-5 gap-6 items-start">
        <div className="lg:col-span-3 w-full max-w-full overflow-x-auto flex justify-center">
          <Calendar
            mode={mode}
            numberOfMonths={2}
            showOutsideDays
            selected={selected}
            onSelect={(d) => setSelected(d)}
            onMonthChange={(m) => fetchPrices(m)}
            disabled={[{ before: new Date() }]}
            components={{ DayContent: renderDay }}
            classNames={{
              head_cell: "text-muted-foreground rounded-md w-9 sm:w-11 font-normal text-[0.8rem]",
              day: "inline-flex flex-col items-center justify-center h-9 w-9 sm:h-11 sm:w-11 p-0 font-normal rounded-md hover:bg-accent hover:text-accent-foreground aria-selected:opacity-100",
              day_outside: "invisible",
            }}
            data-testid="pricing-calendar"
          />
        </div>
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-lg bg-muted px-4 py-3 text-sm" data-testid="pricing-selected-count">
            <span className="font-medium text-foreground">{count}</span> date{count === 1 ? "" : "s"} selected
          </div>
          <div>
            <Label>Nightly price ($)</Label>
            <Input type="number" min="1" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="e.g. 250" className="rounded-lg mt-1" data-testid="pricing-input" />
          </div>
          <Button onClick={apply} disabled={busy || !count} className="w-full rounded-full bg-primary" data-testid="pricing-apply">
            Apply to {count} selected date{count === 1 ? "" : "s"}
          </Button>
          <Button onClick={reset} disabled={busy || !count} variant="outline" className="w-full rounded-full" data-testid="pricing-reset">
            Reset selected to default
          </Button>
          {count > 0 && (
            <Button onClick={clearSelection} variant="ghost" className="w-full rounded-full text-muted-foreground" data-testid="pricing-clear-selection">
              Clear selection
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

export function CalendarSync() {
  const [syncs, setSyncs] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [platform, setPlatform] = useState("airbnb");
  const [syncLabel, setSyncLabel] = useState("");
  const [icalUrl, setIcalUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [block, setBlock] = useState({ start: "", end: "", reason: "" });
  const [exportUrl, setExportUrl] = useState("");
  const [exportFetched, setExportFetched] = useState(null);

  const load = () => {
    api.get("/admin/calendar-sync").then((r) => setSyncs(r.data)).catch(err);
    api.get("/admin/blocks").then((r) => setBlocks(r.data)).catch(err);
    api.get("/admin/calendar-export").then((r) => { setExportUrl(`${API}${r.data.path.replace(/^\/api/, "")}`); setExportFetched(r.data.last_fetched); }).catch(err);
  };
  useEffect(() => { load(); }, []);

  const addSync = async () => {
    const url = (icalUrl || "").trim();
    if (!url) { toast.error("Please paste an iCal (.ics) calendar link"); return; }
    if (!/^https?:\/\/.+\.ics/i.test(url) && !url.toLowerCase().includes(".ics")) { toast.error("That doesn't look like an .ics calendar link"); return; }
    if (platform === "other" && !syncLabel.trim()) { toast.error("Please give this calendar a name"); return; }
    try { await api.post("/admin/calendar-sync", { platform, label: syncLabel.trim(), ical_url: url, active: true }); setIcalUrl(""); setSyncLabel(""); toast.success("Calendar added"); load(); } catch (e) { err(e); }
  };
  const runSync = async (id) => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/calendar-sync/${id}/run`); toast.success(`Synced ${data.event_count} reserved dates`); load(); } catch (e) { err(e); } finally { setBusy(false); }
  };
  const delSync = async (id) => { try { await api.delete(`/admin/calendar-sync/${id}`); load(); } catch (e) { err(e); } };

  const addBlock = async () => {
    if (!block.start || !block.end) return;
    try { await api.post("/admin/blocks", block); setBlock({ start: "", end: "", reason: "" }); toast.success("Dates blocked"); load(); } catch (e) { err(e); }
  };
  const delBlock = async (id) => { try { await api.delete(`/admin/blocks/${id}`); load(); } catch (e) { err(e); } };

  return (
    <div className="space-y-8">
      <h1 className="font-serif text-3xl font-semibold text-foreground">Calendar & Sync</h1>

      <PricingCalendar />

      <Card>
        <div className="flex items-center gap-2 mb-4"><Link2 className="h-5 w-5 text-primary" /><h2 className="font-semibold text-foreground">Import calendars from other platforms</h2></div>
        <p className="text-sm text-muted-foreground mb-4">Paste an iCal (.ics) export link from any platform — Airbnb, VRBO, Booking.com, or any other that provides one. Synced reservations block those dates automatically.</p>
        <div className="flex items-center gap-2 mb-4 rounded-lg bg-secondary/10 text-secondary px-3 py-2 text-xs font-medium" data-testid="auto-sync-note">
          <RefreshCw className="h-3.5 w-3.5 shrink-0" />
          <span>Auto-sync is on — all active calendars refresh automatically every day. Use the ↻ button any time you want to sync right now.</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger className="sm:w-40" data-testid="sync-platform"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="airbnb">Airbnb</SelectItem><SelectItem value="vrbo">VRBO</SelectItem><SelectItem value="booking">Booking.com</SelectItem><SelectItem value="other">Other…</SelectItem></SelectContent>
          </Select>
          {platform === "other" && (
            <Input placeholder="Calendar name (e.g. Host share)" value={syncLabel} onChange={(e) => setSyncLabel(e.target.value)} className="sm:w-48 rounded-lg" data-testid="sync-label-input" />
          )}
          <Input placeholder="https://…/calendar.ics" value={icalUrl} onChange={(e) => setIcalUrl(e.target.value)} className="flex-1 rounded-lg" data-testid="sync-url-input" />
          <Button onClick={addSync} className="rounded-lg bg-primary" data-testid="sync-add-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </div>
        <div className="space-y-2">
          {syncs.map((s) => (
            <div key={s.id} className="flex items-center justify-between bg-muted rounded-lg px-4 py-3" data-testid={`sync-row-${s.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-foreground capitalize flex items-center gap-2">
                  {s.label || s.platform}
                  {s.last_error
                    ? <Badge variant="destructive" className="text-xs"><AlertCircle className="h-3 w-3 mr-1" />error</Badge>
                    : s.last_synced
                      ? <Badge className={`border-0 text-xs ${hoursSince(s.last_synced) > 48 ? "bg-amber-100 text-amber-700" : "bg-secondary/20 text-secondary"}`}><Check className="h-3 w-3 mr-1" />{s.event_count} dates · synced {timeAgo(s.last_synced)}</Badge>
                      : <Badge className="bg-muted text-muted-foreground border-0 text-xs">never synced</Badge>}
                </div>
                <div className="text-xs text-muted-foreground truncate max-w-md">{s.ical_url}</div>
                {s.last_error && <div className="text-xs text-destructive mt-0.5">{s.last_error}</div>}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={() => runSync(s.id)} disabled={busy} data-testid={`sync-run-${s.id}`}><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /></Button>
                <Button size="sm" variant="ghost" onClick={() => delSync(s.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
          {syncs.length === 0 && <p className="text-sm text-muted-foreground">No calendars connected yet.</p>}
        </div>
      </Card>

      <Card data-testid="export-feed-card">
        <div className="flex items-center gap-2 mb-2"><CalendarRange className="h-5 w-5 text-primary" /><h2 className="font-semibold text-foreground">Your universal calendar link</h2></div>
        <p className="text-sm text-muted-foreground mb-4">This is a standard iCal (.ics) link that works with <strong>any</strong> platform that supports calendar import — Airbnb, VRBO, Booking.com, Google Calendar, Hospitable, Lodgify, and more. Paste it into each platform so they automatically block any dates booked directly on your site. It only shows dates as "Reserved" — never guest names or details.</p>
        <div className="flex gap-2 mb-3">
          <Input readOnly value={exportUrl} className="rounded-lg text-xs font-mono" data-testid="export-feed-url" onFocus={(e) => e.target.select()} />
          <Button variant="secondary" className="rounded-lg whitespace-nowrap" data-testid="export-feed-copy" onClick={() => { navigator.clipboard?.writeText(exportUrl); toast.success("Calendar link copied"); }}><Copy className="h-4 w-4 mr-1" /> Copy</Button>
        </div>
        <div className="flex items-center gap-2 mb-4 text-xs" data-testid="export-feed-health">
          <span className={`h-2 w-2 rounded-full ${exportFetched ? "bg-secondary" : "bg-muted-foreground/40"}`} />
          <span className="text-muted-foreground">{exportFetched ? `Last read by a platform ${timeAgo(exportFetched)}` : "Not read by any platform yet — add the link to your platforms below."}</span>
        </div>
        <div className="rounded-lg bg-accent/50 p-4 text-sm text-muted-foreground space-y-2">
          <p className="font-medium text-foreground">How to connect it (one-time setup per platform):</p>
          <p><strong className="text-foreground">Airbnb:</strong> Calendar → Availability → <em>Connect calendars</em> → <em>Import Calendar</em> → paste this link, name it "Direct site".</p>
          <p><strong className="text-foreground">VRBO:</strong> Calendar → <em>Import calendar</em> → paste this link and save.</p>
          <p><strong className="text-foreground">Booking.com:</strong> Rates &amp; Availability → <em>Sync calendars</em> → <em>Import calendar</em> → paste this link.</p>
          <p><strong className="text-foreground">Google Calendar / others:</strong> Use "Add / subscribe from URL" and paste this link.</p>
          <p className="text-xs">Most platforms refresh imported calendars every few hours, so newly-booked dates block automatically (not instantly).</p>
        </div>
      </Card>

      <Card>
        <div className="flex items-center gap-2 mb-4"><CalendarDays className="h-5 w-5 text-primary" /><h2 className="font-semibold text-foreground">Manually blocked dates</h2></div>
        <div className="grid sm:grid-cols-4 gap-3 mb-4">
          <div><Label className="text-xs">From</Label><Input type="date" value={block.start} onChange={(e) => setBlock({ ...block, start: e.target.value })} className="rounded-lg" data-testid="block-start" /></div>
          <div><Label className="text-xs">To (checkout)</Label><Input type="date" value={block.end} onChange={(e) => setBlock({ ...block, end: e.target.value })} className="rounded-lg" data-testid="block-end" /></div>
          <div><Label className="text-xs">Reason</Label><Input value={block.reason} onChange={(e) => setBlock({ ...block, reason: e.target.value })} placeholder="Maintenance" className="rounded-lg" data-testid="block-reason" /></div>
          <div className="flex items-end"><Button onClick={addBlock} className="w-full rounded-lg bg-primary" data-testid="block-add-btn"><Plus className="h-4 w-4 mr-1" /> Block</Button></div>
        </div>
        <div className="space-y-2">
          {blocks.map((b) => (
            <div key={b.id} className="flex items-center justify-between bg-muted rounded-lg px-4 py-2.5 text-sm" data-testid={`block-row-${b.id}`}>
              <span className="text-foreground">{b.start} → {b.end} <span className="text-muted-foreground">· {b.reason}</span></span>
              <Button size="sm" variant="ghost" onClick={() => delBlock(b.id)} className="text-destructive h-7"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          {blocks.length === 0 && <p className="text-sm text-muted-foreground">No manual blocks.</p>}
        </div>
      </Card>
    </div>
  );
}

/* ---------------- Promo codes ---------------- */
export function Promos() {
  const [promos, setPromos] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", discount_type: "percent", amount: 10, min_nights: 1, active: true, expires_at: "" });
  const load = () => api.get("/admin/promos").then((r) => setPromos(r.data)).catch(err);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, amount: Number(form.amount), min_nights: Number(form.min_nights), expires_at: form.expires_at || null };
      await api.post("/admin/promos", payload);
      toast.success("Promo created"); setOpen(false); setForm({ code: "", discount_type: "percent", amount: 10, min_nights: 1, active: true, expires_at: "" }); load();
    } catch (e) { err(e); }
  };
  const del = async (id) => { try { await api.delete(`/admin/promos/${id}`); load(); } catch (e) { err(e); } };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-serif text-3xl font-semibold text-foreground">Promo Codes</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full bg-primary" data-testid="add-promo-btn"><Plus className="h-4 w-4 mr-1" /> New code</Button></DialogTrigger>
          <DialogContent data-testid="promo-dialog">
            <DialogHeader><DialogTitle className="font-serif text-2xl">New promo code</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-lg uppercase" placeholder="SUMMER10" data-testid="promo-code-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label>
                  <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                    <SelectTrigger className="rounded-lg" data-testid="promo-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="percent">Percent %</SelectItem><SelectItem value="fixed">Fixed $</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Amount</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-lg" data-testid="promo-amount" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Min nights</Label><Input type="number" value={form.min_nights} onChange={(e) => setForm({ ...form, min_nights: e.target.value })} className="rounded-lg" /></div>
                <div><Label>Expires (optional)</Label><Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="rounded-lg" /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={save} className="rounded-full bg-primary w-full" data-testid="promo-save">Create code</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Discount</TableHead><TableHead>Min nights</TableHead><TableHead>Expires</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {promos.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No promo codes</TableCell></TableRow>}
            {promos.map((p) => (
              <TableRow key={p.id} data-testid={`promo-row-${p.code}`}>
                <TableCell className="font-mono font-medium">{p.code}</TableCell>
                <TableCell>{p.discount_type === "percent" ? `${p.amount}%` : money(p.amount)}</TableCell>
                <TableCell>{p.min_nights}</TableCell>
                <TableCell>{p.expires_at || "—"}</TableCell>
                <TableCell>{p.active ? <Badge className="bg-secondary/20 text-secondary border-0">Active</Badge> : <Badge variant="secondary">Off</Badge>}</TableCell>
                <TableCell><Button size="sm" variant="ghost" onClick={() => del(p.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/* ---------------- Local Events / Holidays ---------------- */
const EMPTY_EVENT = { name: "", start: "", end: "", icon: "star", repeat_yearly: true };
export function Events() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_EVENT);
  const load = () => api.get("/admin/events").then((r) => setEvents(r.data)).catch(err);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(EMPTY_EVENT); setOpen(true); };
  const openEdit = (ev) => { setEditId(ev.id); setForm({ name: ev.name, start: ev.start, end: ev.end, icon: ev.icon || "star", repeat_yearly: !!ev.repeat_yearly }); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.start) { toast.error("Name and start date are required"); return; }
    const payload = { ...form, end: form.end || form.start };
    if (payload.end < payload.start) { toast.error("End date must be on or after start date"); return; }
    try {
      if (editId) await api.put(`/admin/events/${editId}`, payload);
      else await api.post("/admin/events", payload);
      toast.success(editId ? "Event updated" : "Event added"); setOpen(false); setForm(EMPTY_EVENT); setEditId(null); load();
    } catch (e) { err(e); }
  };
  const del = async (id) => { try { await api.delete(`/admin/events/${id}`); load(); } catch (e) { err(e); } };

  const fmtRange = (ev) => {
    const opts = { month: "short", day: "numeric" };
    const s = new Date(ev.start + "T00:00:00").toLocaleDateString(undefined, opts);
    const e = new Date(ev.end + "T00:00:00").toLocaleDateString(undefined, opts);
    return ev.start === ev.end ? s : `${s} – ${e}`;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <h1 className="font-serif text-3xl font-semibold text-foreground">Local Events & Holidays</h1>
        <Button className="rounded-full bg-primary" data-testid="add-event-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1" /> Add event</Button>
      </div>
      <p className="text-muted-foreground mb-6">Add local holidays, festivals or events. They appear as an icon on your guest booking calendar. Turn on repeat to show them every year.</p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="event-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit event" : "New event"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Event name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg" placeholder="Lost Dutchman Days" data-testid="event-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start date</Label><Input type="date" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="rounded-lg" data-testid="event-start-input" /></div>
              <div><Label>End date</Label><Input type="date" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="rounded-lg" data-testid="event-end-input" /></div>
            </div>
            <div>
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="event-icon-picker">
                {EVENT_ICON_NAMES.map((name) => {
                  const Ico = EVENT_ICONS[name];
                  const active = form.icon === name;
                  return (
                    <button key={name} type="button" data-testid={`event-icon-${name}`} onClick={() => setForm({ ...form, icon: name })}
                      className={`h-10 w-10 flex items-center justify-center rounded-lg border transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>
                      <Ico className="h-5 w-5" />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div><Label className="cursor-pointer">Repeat every year</Label><div className="text-xs text-muted-foreground">Shows on the same dates annually.</div></div>
              <Switch checked={form.repeat_yearly} onCheckedChange={(v) => setForm({ ...form, repeat_yearly: v })} data-testid="event-repeat-switch" />
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-primary w-full" data-testid="event-save">{editId ? "Save changes" : "Add event"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead></TableHead><TableHead>Event</TableHead><TableHead>Dates</TableHead><TableHead>Repeat</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {events.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No events yet</TableCell></TableRow>}
            {events.map((ev) => {
              const Ico = EVENT_ICONS[ev.icon] || EVENT_ICONS.star;
              return (
                <TableRow key={ev.id} data-testid={`event-row-${ev.id}`}>
                  <TableCell><Ico className="h-5 w-5 text-primary" /></TableCell>
                  <TableCell className="font-medium">{ev.name}</TableCell>
                  <TableCell>{fmtRange(ev)}</TableCell>
                  <TableCell>{ev.repeat_yearly ? <Badge className="bg-secondary/20 text-secondary border-0">Yearly</Badge> : <Badge variant="secondary">One-time</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(ev)} data-testid={`event-edit-${ev.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(ev.id)} className="text-destructive" data-testid={`event-delete-${ev.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/* ---------------- Reviews ---------------- */
export function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ author: "", location: "", rating: 5, text: "", featured: false, approved: true });
  const load = () => api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(err);
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.post("/admin/reviews", { ...form, rating: Number(form.rating) }); toast.success("Review added"); setOpen(false); setForm({ author: "", location: "", rating: 5, text: "", featured: false, approved: true }); load(); } catch (e) { err(e); }
  };
  const del = async (id) => { try { await api.delete(`/admin/reviews/${id}`); load(); } catch (e) { err(e); } };
  const toggleApprove = async (r) => { try { await api.patch(`/admin/reviews/${r.id}/approve`, { approved: !r.approved }); load(); } catch (e) { err(e); } };
  const pendingCount = reviews.filter((r) => !r.approved).length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="font-serif text-3xl font-semibold text-foreground">Reviews</h1>
          {pendingCount > 0 && <Badge className="bg-amber-500/15 text-amber-600 border-0" data-testid="pending-reviews-count">{pendingCount} pending approval</Badge>}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full bg-primary" data-testid="add-review-btn"><Plus className="h-4 w-4 mr-1" /> Add review</Button></DialogTrigger>
          <DialogContent data-testid="review-dialog">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Add a review</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Author</Label><Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="rounded-lg" data-testid="review-author" /></div>
                <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-lg" /></div>
              </div>
              <div><Label>Rating</Label>
                <Select value={String(form.rating)} onValueChange={(v) => setForm({ ...form, rating: v })}>
                  <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>{[5,4,3,2,1].map((n) => <SelectItem key={n} value={String(n)}>{n} stars</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Review</Label><Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="rounded-lg" data-testid="review-text" /></div>
            </div>
            <DialogFooter><Button onClick={save} className="rounded-full bg-primary w-full" data-testid="review-save">Add review</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {reviews.map((r) => (
          <Card key={r.id} data-testid={`review-admin-${r.id}`} className={!r.approved ? "border-amber-400/60 bg-amber-50/40" : ""}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-medium text-foreground flex items-center gap-2">
                  {r.author}
                  {r.verified && <Badge className="bg-primary/15 text-primary border-0 text-[10px]">Verified guest</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">{r.location ? `${r.location} · ` : ""}{r.rating}★</div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant={r.approved ? "outline" : "default"} onClick={() => toggleApprove(r)}
                  className={`rounded-full text-xs ${r.approved ? "" : "bg-primary"}`} data-testid={`review-approve-${r.id}`}>
                  {r.approved ? <><EyeOff className="h-3.5 w-3.5 mr-1" /> Hide</> : <><Check className="h-3.5 w-3.5 mr-1" /> Approve</>}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => del(r.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">{r.text}</p>
            {!r.approved && <p className="text-xs text-amber-600 mt-2 font-medium">Not shown publicly until approved.</p>}
          </Card>
        ))}
        {reviews.length === 0 && <p className="text-sm text-muted-foreground">No reviews yet.</p>}
      </div>
    </div>
  );
}

/* ---------------- Inquiries ---------------- */
export function Inquiries() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/inquiries").then((r) => setItems(r.data)).catch(err);
  useEffect(() => { load(); }, []);
  const mark = async (id) => { try { await api.patch(`/admin/inquiries/${id}`, { status: "replied" }); load(); } catch (e) { err(e); } };
  const del = async (id) => { try { await api.delete(`/admin/inquiries/${id}`); load(); } catch (e) { err(e); } };
  return (
    <div>
      <h1 className="font-serif text-3xl font-semibold text-foreground mb-6">Inquiries</h1>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-muted-foreground">No inquiries yet.</p>}
        {items.map((i) => (
          <Card key={i.id} data-testid={`inquiry-${i.id}`}>
            <div className="flex justify-between items-start gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">{i.name}</span>
                  {i.status === "new" ? <Badge className="bg-primary/15 text-primary border-0">New</Badge> : <Badge variant="secondary">Replied</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">{i.email}{i.phone && ` · ${i.phone}`}</div>
                <p className="text-sm text-foreground mt-2">{i.message}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <OfferDialog
                  initial={{ guest_name: i.name, guest_email: i.email, guest_phone: i.phone || "", check_in: i.check_in || "", check_out: i.check_out || "", guests: i.guests || 2, inquiry_id: i.id }}
                  onCreated={load}
                  trigger={<Button size="sm" variant="outline" className="text-primary border-primary/40" data-testid={`inquiry-offer-${i.id}`}><Tag className="h-3.5 w-3.5 mr-1" /> Send offer</Button>}
                />
                {i.status === "new" && <Button size="sm" variant="outline" onClick={() => mark(i.id)} data-testid={`inquiry-reply-${i.id}`}>Mark replied</Button>}
                <Button size="sm" variant="ghost" onClick={() => del(i.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Upsells / Add-ons ---------------- */
export function Upsells() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", price: 25, icon: "sparkles", active: true });
  const load = () => api.get("/admin/upsells").then((r) => setItems(r.data)).catch(err);
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.post("/admin/upsells", { ...form, price: Number(form.price) }); toast.success("Add-on created"); setOpen(false); setForm({ name: "", description: "", price: 25, icon: "sparkles", active: true }); load(); } catch (e) { err(e); }
  };
  const toggle = async (u) => { try { await api.put(`/admin/upsells/${u.id}`, { ...u, active: !u.active }); load(); } catch (e) { err(e); } };
  const del = async (id) => { try { await api.delete(`/admin/upsells/${id}`); load(); } catch (e) { err(e); } };

  const ICONS = ["sparkles", "clock", "car", "utensils", "waves", "coffee", "baby"];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-foreground">Add-ons & Upsells</h1>
          <p className="text-sm text-muted-foreground mt-1">Guests select these at checkout and pay upfront with their deposit.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full bg-primary" data-testid="add-upsell-btn"><Plus className="h-4 w-4 mr-1" /> New add-on</Button></DialogTrigger>
          <DialogContent data-testid="upsell-dialog">
            <DialogHeader><DialogTitle className="font-serif text-2xl">New add-on</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg" placeholder="Early check-in" data-testid="upsell-name" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-lg" placeholder="Arrive 4 hours early..." /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Price ($)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-lg" data-testid="upsell-price" /></div>
                <div><Label>Icon</Label>
                  <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                    <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>{ICONS.map((i) => <SelectItem key={i} value={i} className="capitalize">{i}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={save} className="rounded-full bg-primary w-full" data-testid="upsell-save">Create add-on</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.length === 0 && <p className="text-sm text-muted-foreground">No add-ons yet.</p>}
        {items.map((u) => (
          <Card key={u.id} data-testid={`upsell-admin-${u.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <div className="font-medium text-foreground">{u.name} · <span className="text-primary">{money(u.price)}</span></div>
                <p className="text-sm text-muted-foreground mt-1">{u.description}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Switch checked={u.active} onCheckedChange={() => toggle(u)} data-testid={`upsell-toggle-${u.id}`} />
                <Button size="sm" variant="ghost" onClick={() => del(u.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Seasonal / date-based rates ---------------- */
export function Rates() {
  const [rules, setRules] = useState([]);
  const [base, setBase] = useState(null);
  const [form, setForm] = useState({ name: "", start: "", end: "", nightly_rate: "", min_nights: "", airbnb_rate: "", vrbo_rate: "" });
  const load = () => {
    api.get("/admin/rates").then((r) => setRules(r.data)).catch(err);
    api.get("/property").then((r) => {
      setBase(r.data.pricing || {});
    }).catch(err);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const start = (form.start || "").trim();
    const end = (form.end || "").trim();
    const rate = parseFloat(form.nightly_rate);
    if (!start || !end) { toast.error("Please choose both a start and end date"); return; }
    if (!form.nightly_rate || Number.isNaN(rate) || rate <= 0) { toast.error("Please enter a nightly rate"); return; }
    if (new Date(end) < new Date(start)) { toast.error("End date must be on or after the start date"); return; }
    try {
      await api.post("/admin/rates", {
        name: (form.name || "").trim(),
        start,
        end,
        nightly_rate: rate,
        min_nights: form.min_nights ? Number(form.min_nights) : null,
        airbnb_rate: form.airbnb_rate ? parseFloat(form.airbnb_rate) : null,
        vrbo_rate: form.vrbo_rate ? parseFloat(form.vrbo_rate) : null,
      });
      toast.success("Rate added");
      setForm({ name: "", start: "", end: "", nightly_rate: "", min_nights: "", airbnb_rate: "", vrbo_rate: "" });
      load();
    } catch (e) { err(e); }
  };
  const del = async (id) => { try { await api.delete(`/admin/rates/${id}`); load(); } catch (e) { err(e); } };

  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({ name: "", start: "", end: "", nightly_rate: "", min_nights: "", airbnb_rate: "", vrbo_rate: "" });
  const startEdit = (r) => {
    setEditingId(r.id);
    setEdit({ name: r.name || "", start: r.start, end: r.end, nightly_rate: String(r.nightly_rate), min_nights: r.min_nights != null ? String(r.min_nights) : "", airbnb_rate: r.airbnb_rate != null ? String(r.airbnb_rate) : "", vrbo_rate: r.vrbo_rate != null ? String(r.vrbo_rate) : "" });
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async (id) => {
    const start = (edit.start || "").trim();
    const end = (edit.end || "").trim();
    const rate = parseFloat(edit.nightly_rate);
    if (!start || !end) { toast.error("Please choose both a start and end date"); return; }
    if (!edit.nightly_rate || Number.isNaN(rate) || rate <= 0) { toast.error("Please enter a nightly rate"); return; }
    if (new Date(end) < new Date(start)) { toast.error("End date must be on or after the start date"); return; }
    try {
      await api.put(`/admin/rates/${id}`, {
        name: (edit.name || "").trim(),
        start,
        end,
        nightly_rate: rate,
        min_nights: edit.min_nights ? Number(edit.min_nights) : null,
        airbnb_rate: edit.airbnb_rate ? parseFloat(edit.airbnb_rate) : null,
        vrbo_rate: edit.vrbo_rate ? parseFloat(edit.vrbo_rate) : null,
      });
      toast.success("Rate updated");
      setEditingId(null);
      load();
    } catch (e) { err(e); }
  };

  return (
    <div>
      <h1 className="font-serif text-3xl font-semibold text-foreground mb-1">Rates & Seasons</h1>
      <p className="text-sm text-muted-foreground mb-6">Set custom nightly prices for specific date ranges (holidays, peak season). Nights outside any range use your base rate{base ? ` of ${money(base.nightly_rate)}/night` : ""}.</p>

      <Card className="mb-6">
        <h2 className="font-semibold text-foreground mb-4">Add a custom rate</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div className="lg:col-span-2"><Label className="text-xs">Label</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Holiday season" className="rounded-lg" data-testid="rate-name" /></div>
          <div><Label className="text-xs">From</Label><Input type="date" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="rounded-lg" data-testid="rate-start" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="rounded-lg" data-testid="rate-end" /></div>
          <div><Label className="text-xs">Direct rate ($/night)</Label><Input type="number" value={form.nightly_rate} onChange={(e) => setForm({ ...form, nightly_rate: e.target.value })} className="rounded-lg" data-testid="rate-price" /></div>
          <div><Label className="text-xs">Airbnb rate ($/night)</Label><Input type="number" value={form.airbnb_rate} onChange={(e) => setForm({ ...form, airbnb_rate: e.target.value })} placeholder="—" className="rounded-lg" data-testid="rate-airbnb" /></div>
          <div><Label className="text-xs">VRBO rate ($/night)</Label><Input type="number" value={form.vrbo_rate} onChange={(e) => setForm({ ...form, vrbo_rate: e.target.value })} placeholder="—" className="rounded-lg" data-testid="rate-vrbo" /></div>
          <div><Label className="text-xs">Min nights</Label><Input type="number" value={form.min_nights} onChange={(e) => setForm({ ...form, min_nights: e.target.value })} placeholder="—" className="rounded-lg" /></div>
        </div>
        <Button onClick={add} className="rounded-lg bg-primary mt-4" data-testid="rate-add-btn"><Plus className="h-4 w-4 mr-1" /> Add rate</Button>
      </Card>

      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Label</TableHead><TableHead>Dates</TableHead><TableHead>Direct rate</TableHead><TableHead>Airbnb rate</TableHead><TableHead>VRBO rate</TableHead><TableHead>Min nights</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rules.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No custom rates — all dates use the base rate</TableCell></TableRow>}
            {rules.map((r) => (
              editingId === r.id ? (
                <TableRow key={r.id} data-testid={`rate-row-${r.id}`} className="bg-muted/40">
                  <TableCell><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Label" className="rounded-lg h-9" data-testid="rate-edit-name" /></TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Input type="date" value={edit.start} onChange={(e) => setEdit({ ...edit, start: e.target.value })} className="rounded-lg h-9" data-testid="rate-edit-start" />
                      <Input type="date" value={edit.end} onChange={(e) => setEdit({ ...edit, end: e.target.value })} className="rounded-lg h-9" data-testid="rate-edit-end" />
                    </div>
                  </TableCell>
                  <TableCell><Input type="number" value={edit.nightly_rate} onChange={(e) => setEdit({ ...edit, nightly_rate: e.target.value })} className="rounded-lg h-9 w-24" data-testid="rate-edit-price" /></TableCell>
                  <TableCell><Input type="number" value={edit.airbnb_rate} onChange={(e) => setEdit({ ...edit, airbnb_rate: e.target.value })} placeholder="—" className="rounded-lg h-9 w-24" data-testid="rate-edit-airbnb" /></TableCell>
                  <TableCell><Input type="number" value={edit.vrbo_rate} onChange={(e) => setEdit({ ...edit, vrbo_rate: e.target.value })} placeholder="—" className="rounded-lg h-9 w-24" data-testid="rate-edit-vrbo" /></TableCell>
                  <TableCell><Input type="number" value={edit.min_nights} onChange={(e) => setEdit({ ...edit, min_nights: e.target.value })} placeholder="—" className="rounded-lg h-9 w-20" data-testid="rate-edit-min" /></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => saveEdit(r.id)} className="rounded-lg bg-primary h-8" data-testid={`rate-save-${r.id}`}><Check className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-8" data-testid={`rate-cancel-${r.id}`}><X className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={r.id} data-testid={`rate-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name || "—"}</TableCell>
                  <TableCell className="text-sm">{r.start} → {r.end}</TableCell>
                  <TableCell className="font-semibold text-primary">{money(r.nightly_rate)}</TableCell>
                  <TableCell>{r.airbnb_rate != null ? money(r.airbnb_rate) : "—"}</TableCell>
                  <TableCell>{r.vrbo_rate != null ? money(r.vrbo_rate) : "—"}</TableCell>
                  <TableCell>{r.min_nights || "—"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(r)} className="text-muted-foreground hover:text-foreground" data-testid={`rate-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(r.id)} className="text-destructive" data-testid={`rate-del-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/* ---------------- Photos ---------------- */
export function Photos() {
  const [images, setImages] = useState([]); // [{url, caption}]
  const [hero, setHero] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dragIndex = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/property").then((r) => {
      setImages(r.data.images || []);
      setHero(r.data.hero_image || (r.data.images || [])[0]?.url || "");
    }).catch(err);
  }, []);

  const commit = (imgs, h) => { setImages(imgs); if (h !== undefined) setHero(h); setDirty(true); };

  const move = (from, to) => {
    if (to < 0 || to >= images.length) return;
    const arr = [...images];
    const [it] = arr.splice(from, 1);
    arr.splice(to, 0, it);
    commit(arr);
  };
  const onDrop = (to) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === to) return;
    move(from, to);
  };
  const remove = (i) => {
    const removed = images[i];
    const arr = images.filter((_, idx) => idx !== i);
    const nextHero = removed.url === hero ? (arr[0]?.url || "") : hero;
    commit(arr, nextHero);
  };
  const setMain = (url) => { setHero(url); setDirty(true); };
  const setCaption = (i, caption) => {
    const arr = images.map((p, idx) => (idx === i ? { ...p, caption } : p));
    commit(arr);
  };
  const addUrl = () => {
    const u = newUrl.trim();
    if (!u) return;
    const arr = [...images, { url: u, caption: "" }];
    commit(arr, hero || u);
    setNewUrl("");
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const url = `${API}/media/${data.path}`;
        added.push({ url, caption: "" });
      }
      const arr = [...images, ...added];
      commit(arr, hero || added[0]?.url);
      toast.success(`${added.length} photo${added.length > 1 ? "s" : ""} uploaded`);
    } catch (e2) { err(e2); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/property", { images, hero_image: hero || images[0]?.url || "" });
      toast.success("Photos saved");
      setDirty(false);
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-foreground">Photos</h1>
          <p className="text-sm text-muted-foreground mt-1">Drag to reorder. The <span className="text-primary font-medium">Main</span> photo is your homepage hero. Captions show at the bottom of each photo.</p>
        </div>
        <Button onClick={save} disabled={saving || !dirty} className="rounded-full bg-primary shrink-0" data-testid="photos-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : dirty ? "Save changes" : "Saved"}
        </Button>
      </div>

      <Card className="mt-5">
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onUpload} data-testid="photo-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-lg bg-primary" data-testid="upload-photo-btn">
            {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading...</> : <><ImageIcon className="h-4 w-4 mr-1" /> Upload from computer</>}
          </Button>
          <div className="flex gap-2 flex-1">
            <Input placeholder="…or paste an image URL" value={newUrl} onChange={(e) => setNewUrl(e.target.value)} className="rounded-lg" data-testid="add-photo-url" />
            <Button onClick={addUrl} disabled={!newUrl.trim()} variant="secondary" className="rounded-lg" data-testid="add-photo-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((photo, i) => {
            const isMain = photo.url === hero;
            return (
              <div
                key={photo.url + i}
                data-testid={`photo-item-${i}`}
                draggable
                onDragStart={() => { dragIndex.current = i; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(i)}
                className={`rounded-xl overflow-hidden border-2 bg-muted ${isMain ? "border-primary ring-2 ring-primary/30" : "border-transparent"}`}
              >
                <div className="relative group cursor-grab active:cursor-grabbing">
                  <img src={photo.url} alt={photo.caption || `Photo ${i + 1}`} loading="lazy" className="w-full h-40 object-cover" />
                  <div className="absolute top-1.5 left-1.5 flex items-center gap-1">
                    <span className="bg-background/85 rounded px-1.5 py-0.5 text-[11px] font-medium">{i + 1}</span>
                    {isMain && <span className="bg-primary text-white rounded px-1.5 py-0.5 text-[11px] font-semibold flex items-center gap-0.5"><Star className="h-3 w-3 fill-white" /> Main</span>}
                  </div>
                  <div className="absolute top-1.5 right-1.5 opacity-40 group-hover:opacity-100 transition-opacity"><GripVertical className="h-4 w-4 text-white drop-shadow" /></div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/80 to-transparent p-2 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex gap-1">
                      <button data-testid={`photo-up-${i}`} onClick={() => move(i, i - 1)} disabled={i === 0} className="h-7 w-7 rounded bg-white/90 flex items-center justify-center disabled:opacity-40 hover:bg-white"><ArrowUp className="h-3.5 w-3.5" /></button>
                      <button data-testid={`photo-down-${i}`} onClick={() => move(i, i + 1)} disabled={i === images.length - 1} className="h-7 w-7 rounded bg-white/90 flex items-center justify-center disabled:opacity-40 hover:bg-white"><ArrowDown className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="flex gap-1">
                      {!isMain && <button data-testid={`photo-set-main-${i}`} onClick={() => setMain(photo.url)} title="Set as main" className="h-7 w-7 rounded bg-white/90 flex items-center justify-center hover:bg-white"><Star className="h-3.5 w-3.5 text-primary" /></button>}
                      <button data-testid={`photo-remove-${i}`} onClick={() => remove(i)} title="Remove" className="h-7 w-7 rounded bg-white/90 flex items-center justify-center hover:bg-white"><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
                    </div>
                  </div>
                </div>
                <div className="p-2">
                  <Input
                    value={photo.caption || ""}
                    onChange={(e) => setCaption(i, e.target.value)}
                    placeholder="Add a label / caption"
                    className="rounded-lg h-9 text-sm"
                    data-testid={`photo-caption-${i}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
        {images.length === 0 && <p className="text-sm text-muted-foreground">No photos yet — upload or add one above.</p>}
      </Card>
    </div>
  );
}

/* ---------------- Content & Pricing ---------------- */
function OwnerLoginCard() {
  const { user, updateCredentials } = useAuth();
  const [cur, setCur] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!cur) { toast.error("Enter your current password"); return; }
    if (!email && !pw) { toast.error("Enter a new email or a new password"); return; }
    if (pw && pw !== pw2) { toast.error("New passwords don't match"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-credentials", {
        current_password: cur, new_email: email || null, new_password: pw || null,
      });
      updateCredentials(data);
      toast.success("Login credentials updated");
      setCur(""); setEmail(""); setPw(""); setPw2("");
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  return (
    <Card className="lg:col-span-2" data-testid="owner-login-card">
      <h2 className="font-semibold text-foreground mb-1">Owner login</h2>
      <p className="text-sm text-muted-foreground mb-4">Change the email and password you use to sign in to this dashboard. You'll stay logged in after saving.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><Label>Current password</Label><Input type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Enter your current password" className="rounded-lg mt-1" data-testid="owner-current-password" /></div>
        <div className="sm:col-span-2"><Label>New login email</Label><Input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={user?.email || "owner@example.com"} className="rounded-lg mt-1" data-testid="owner-new-email" /><p className="text-xs text-muted-foreground mt-1">Leave blank to keep your current email ({user?.email}).</p></div>
        <div><Label>New password</Label><Input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" className="rounded-lg mt-1" data-testid="owner-new-password" /></div>
        <div><Label>Confirm new password</Label><Input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Re-type new password" className="rounded-lg mt-1" data-testid="owner-confirm-password" /></div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={submit} disabled={saving} className="rounded-full bg-primary" data-testid="owner-login-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update login"}</Button>
      </div>
    </Card>
  );
}

export function Content() {
  const [prop, setProp] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/property").then((r) => setProp(r.data)).catch(err); }, []);
  if (!prop) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;

  const setField = (k, v) => setProp({ ...prop, [k]: v });
  const setPrice = (k, v) => setProp({ ...prop, pricing: { ...prop.pricing, [k]: v } });
  const dp = prop.damage_protection || { enabled: false, provider: "", waiver_label: "Damage waiver", deposit_label: "Refundable security deposit", description: "", waiver_amount: 50, security_deposit: 1000 };
  const setDP = (k, v) => setProp({ ...prop, damage_protection: { ...dp, [k]: v } });

  const amenities = prop.amenities || [];
  const setAmenity = (i, k, v) => setProp({ ...prop, amenities: amenities.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)) });
  const addAmenity = () => setProp({ ...prop, amenities: [...amenities, { icon: "sparkles", label: "" }] });
  const removeAmenity = (i) => setProp({ ...prop, amenities: amenities.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/property", {
        name: prop.name, tagline: prop.tagline, description: prop.description, location: prop.location,
        contact_email: prop.contact_email, alert_email_2: prop.alert_email_2 || "", whatsapp_number: prop.whatsapp_number, sms_alert_number: prop.sms_alert_number, sms_alert_number_2: prop.sms_alert_number_2 || "", max_guests: Number(prop.max_guests),
        bedrooms: Number(prop.bedrooms), beds: Number(prop.beds), bathrooms: Number(prop.bathrooms),
        pricing: {
          ...prop.pricing,
          nightly_rate: Number(prop.pricing.nightly_rate), cleaning_fee: Number(prop.pricing.cleaning_fee),
          deposit_amount: Number(prop.pricing.deposit_amount), service_fee: Number(prop.pricing.service_fee || 0),
          tax_percent: Number(prop.pricing.tax_percent || 0),
          min_nights: Number(prop.pricing.min_nights),
        },
        damage_protection: {
          enabled: !!dp.enabled, provider: dp.provider || "RentalGuardian",
          waiver_label: dp.waiver_label || "Damage waiver", deposit_label: dp.deposit_label || "Refundable security deposit",
          description: dp.description || "", waiver_amount: Number(dp.waiver_amount || 0), security_deposit: Number(dp.security_deposit || 0),
          waiver_signup_url: dp.waiver_signup_url || "",
        },
        images: prop.images,
        amenities: amenities.filter((a) => (a.label || "").trim()).map((a) => ({ icon: a.icon || "sparkles", label: a.label.trim() })),
        rental_agreement: prop.rental_agreement || "",
      });
      toast.success("Saved");
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-serif text-3xl font-semibold text-foreground">Content & Pricing</h1>
        <Button onClick={save} disabled={saving} className="rounded-full bg-primary" data-testid="content-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <h2 className="font-semibold text-foreground mb-4">Property info</h2>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={prop.name} onChange={(e) => setField("name", e.target.value)} className="rounded-lg mt-1" data-testid="content-name" /></div>
            <div><Label>Tagline</Label><Input value={prop.tagline} onChange={(e) => setField("tagline", e.target.value)} className="rounded-lg mt-1" /></div>
            <div><Label>Location</Label><Input value={prop.location} onChange={(e) => setField("location", e.target.value)} className="rounded-lg mt-1" /></div>
            <div><Label>Description</Label><Textarea value={prop.description} onChange={(e) => setField("description", e.target.value)} className="rounded-lg mt-1 min-h-[120px]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact email</Label><Input value={prop.contact_email} onChange={(e) => setField("contact_email", e.target.value)} className="rounded-lg mt-1" /></div>
              <div><Label>WhatsApp number</Label><Input value={prop.whatsapp_number} onChange={(e) => setField("whatsapp_number", e.target.value)} className="rounded-lg mt-1" /></div>
            </div>
            <div><Label>SMS alert number (same-day requests)</Label><Input value={prop.sms_alert_number || ""} onChange={(e) => setField("sms_alert_number", e.target.value)} placeholder="+16025551234" className="rounded-lg mt-1" data-testid="content-sms-alert" /><p className="text-xs text-muted-foreground mt-1">We'll text this number the moment a same-day booking request comes in. Use +1 format.</p></div>
            <div className="rounded-lg bg-accent/40 p-3 space-y-3">
              <Label className="text-foreground">Second recipient (optional)</Label>
              <p className="text-xs text-muted-foreground -mt-1">Add a partner or co-host to also receive owner alert emails and texts.</p>
              <div><Label className="text-xs">Additional email</Label><Input value={prop.alert_email_2 || ""} onChange={(e) => setField("alert_email_2", e.target.value)} placeholder="partner@example.com" className="rounded-lg mt-1" data-testid="content-alert-email-2" /></div>
              <div><Label className="text-xs">Additional phone</Label><Input value={prop.sms_alert_number_2 || ""} onChange={(e) => setField("sms_alert_number_2", e.target.value)} placeholder="+16025556789" className="rounded-lg mt-1" data-testid="content-sms-alert-2" /><p className="text-[11px] text-muted-foreground mt-1">Use +1 format. Gets the same owner alert texts.</p></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label>Guests</Label><Input type="number" value={prop.max_guests} onChange={(e) => setField("max_guests", e.target.value)} className="rounded-lg mt-1" /></div>
              <div><Label>Bedrooms</Label><Input type="number" value={prop.bedrooms} onChange={(e) => setField("bedrooms", e.target.value)} className="rounded-lg mt-1" /></div>
              <div><Label>Beds</Label><Input type="number" value={prop.beds} onChange={(e) => setField("beds", e.target.value)} className="rounded-lg mt-1" /></div>
              <div><Label>Baths</Label><Input type="number" value={prop.bathrooms} onChange={(e) => setField("bathrooms", e.target.value)} className="rounded-lg mt-1" /></div>
            </div>
          </div>
        </Card>
        <Card>
          <h2 className="font-semibold text-foreground mb-4">Pricing & deposit</h2>
          <div className="space-y-4">
            <div><Label>Nightly rate ($)</Label><Input type="number" value={prop.pricing.nightly_rate} onChange={(e) => setPrice("nightly_rate", e.target.value)} className="rounded-lg mt-1" data-testid="content-nightly" /></div>
            <div><Label>Cleaning fee ($)</Label><Input type="number" value={prop.pricing.cleaning_fee} onChange={(e) => setPrice("cleaning_fee", e.target.value)} className="rounded-lg mt-1" /></div>
            <div><Label>Local lodging tax (%)</Label><Input type="number" step="0.001" value={prop.pricing.tax_percent ?? 0} onChange={(e) => setPrice("tax_percent", e.target.value)} className="rounded-lg mt-1" data-testid="content-tax" /><p className="text-xs text-muted-foreground mt-1">Apache Junction (Pinal County) is ~11.5%. Applied to the nightly + cleaning subtotal. Set 0 to disable.</p></div>
            <div className="rounded-lg bg-accent/60 p-3 mt-1">
              <Label className="text-foreground">Deposit collected at booking</Label>
              <p className="text-sm text-muted-foreground mt-1">Automatically set to <span className="font-semibold text-primary">50% of the total booking</span> (plus any add-ons). The remaining balance is collected 30 days before check-in.</p>
            </div>
            <div><Label>Minimum nights</Label><Input type="number" value={prop.pricing.min_nights} onChange={(e) => setPrice("min_nights", e.target.value)} className="rounded-lg mt-1" /></div>
          </div>
        </Card>
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-semibold text-foreground">Damage protection (guest choice at checkout)</h2>
            <Switch checked={!!dp.enabled} onCheckedChange={(v) => setDP("enabled", v)} data-testid="dp-enabled-switch" />
          </div>
          <p className="text-sm text-muted-foreground mb-4">When on, guests choose one of two options at checkout: pay a <strong>damage waiver</strong> (a flat fee, non-refundable, collected upfront) OR a <strong>refundable security deposit</strong> (charged with their final balance and refunded after checkout if there's no damage). The waiver is a placeholder until you connect an insurance provider; the security deposit works today.</p>
          <div className={`grid sm:grid-cols-2 gap-4 ${dp.enabled ? "" : "opacity-50 pointer-events-none"}`}>
            <div><Label>Waiver label (guest-facing)</Label><Input value={dp.waiver_label || ""} onChange={(e) => setDP("waiver_label", e.target.value)} placeholder="Damage waiver" className="rounded-lg mt-1" data-testid="dp-waiver-label" /></div>
            <div><Label>Waiver fee ($)</Label><Input type="number" value={dp.waiver_amount ?? ""} onChange={(e) => setDP("waiver_amount", e.target.value)} placeholder="50" className="rounded-lg mt-1" data-testid="dp-waiver-rate" /></div>
            <div><Label>Security deposit label</Label><Input value={dp.deposit_label || ""} onChange={(e) => setDP("deposit_label", e.target.value)} placeholder="Refundable security deposit" className="rounded-lg mt-1" data-testid="dp-deposit-label" /></div>
            <div><Label>Security deposit amount ($)</Label><Input type="number" value={dp.security_deposit ?? ""} onChange={(e) => setDP("security_deposit", e.target.value)} placeholder="1000" className="rounded-lg mt-1" data-testid="dp-deposit-amount" /></div>
            <div className="sm:col-span-2"><Label>Description shown to guests</Label><Input value={dp.description || ""} onChange={(e) => setDP("description", e.target.value)} placeholder="Choose how you'd like to cover potential damages." className="rounded-lg mt-1" data-testid="dp-description" /></div>
            <div className="sm:col-span-2"><Label>Insurance provider (reference)</Label><Input value={dp.provider || ""} onChange={(e) => setDP("provider", e.target.value)} placeholder="e.g. RentalGuardian" className="rounded-lg mt-1" data-testid="dp-provider" /></div>
            <div className="sm:col-span-2"><Label>Waiver sign-up link (RentalGuardian enrollment URL)</Label><Input value={dp.waiver_signup_url || ""} onChange={(e) => setDP("waiver_signup_url", e.target.value)} placeholder="https://rentalguardian.com/enroll/..." className="rounded-lg mt-1" data-testid="dp-waiver-signup-url" /><p className="text-xs text-muted-foreground mt-1">Guests who select the damage waiver get an email from frank@myazbnb.com with this link to activate their coverage.</p></div>
          </div>
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-accent/60 px-3 py-2 text-xs text-muted-foreground">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span>The refundable security deposit is charged as part of the guest's balance — remember to refund it in Stripe after checkout if there's no damage. The waiver fee issues no policy until a provider API is connected.</span>
          </div>
        </Card>

        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-semibold text-foreground">Thoughtful amenities</h2>
            <Button type="button" variant="secondary" size="sm" className="rounded-full" onClick={addAmenity} data-testid="add-amenity-btn"><Plus className="h-4 w-4 mr-1" /> Add amenity</Button>
          </div>
          <p className="text-sm text-muted-foreground mb-4">These appear in the "Thoughtful amenities" section on your site. Pick an icon and give each a short label.</p>
          {amenities.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No amenities yet — add your first one.</p>}
          <div className="grid sm:grid-cols-2 gap-3">
            {amenities.map((a, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg border border-border p-2" data-testid={`amenity-editor-row-${i}`}>
                <span className="h-9 w-9 shrink-0 rounded-full bg-secondary/20 flex items-center justify-center">
                  <AmenityIcon name={a.icon} className="h-4 w-4 text-secondary" />
                </span>
                <Select value={a.icon || "sparkles"} onValueChange={(v) => setAmenity(i, "icon", v)}>
                  <SelectTrigger className="w-32 rounded-lg h-9 shrink-0" data-testid={`amenity-icon-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AMENITY_ICONS.map((ic) => (
                      <SelectItem key={ic} value={ic}>
                        <span className="flex items-center gap-2"><AmenityIcon name={ic} className="h-4 w-4 text-secondary" /> {ic}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input value={a.label || ""} onChange={(e) => setAmenity(i, "label", e.target.value)} placeholder="e.g. Fast Wi-Fi" className="rounded-lg h-9 flex-1" data-testid={`amenity-label-${i}`} />
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-destructive" onClick={() => removeAmenity(i)} data-testid={`amenity-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </Card>

        <OwnerLoginCard />

        <Card className="lg:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <h2 className="font-semibold text-foreground">Rental agreement</h2>
            </div>
            <Button onClick={save} disabled={saving} size="sm" className="rounded-full bg-primary" data-testid="agreement-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Every guest must read and electronically sign this agreement (checkbox + typed full name) before they can pay — on the main booking form and when accepting a special offer. Their signature and timestamp are saved on each booking for your insurance records. Edit the text below to match your policy.
          </p>
          <Textarea
            value={prop.rental_agreement || ""}
            onChange={(e) => setField("rental_agreement", e.target.value)}
            className="rounded-lg mt-1 min-h-[360px] font-mono text-xs leading-relaxed"
            data-testid="content-rental-agreement"
            placeholder="Paste or write your full rental agreement here…"
          />
          <p className="text-[11px] text-muted-foreground mt-2">Tip: review with your own attorney. This is a starter template, not legal advice.</p>
        </Card>
      </div>
    </div>
  );
}


/* ---------------- Special Offers ---------------- */
const OFFER_STATUS_COLORS = {
  sent: "bg-primary/15 text-primary",
  accepted: "bg-secondary/20 text-secondary",
  expired: "bg-muted text-muted-foreground",
  cancelled: "bg-muted text-muted-foreground",
};

export function OfferDialog({ trigger, initial = {}, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const blank = { guest_name: "", guest_email: "", guest_phone: "", check_in: "", check_out: "", guests: 2, total_price: "", cleaning_fee_enabled: true, cleaning_fee: "", damage_coverage: "none", expires_in_days: 7, note: "", inquiry_id: null };
  const [form, setForm] = useState({ ...blank, ...initial });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setForm({ ...blank, ...initial }); }, [open]);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async () => {
    if (!form.guest_name || !form.guest_email) { toast.error("Enter the guest's name and email"); return; }
    if (!form.check_in || !form.check_out) { toast.error("Choose check-in and check-out dates"); return; }
    const price = parseFloat(form.total_price);
    if (!form.total_price || Number.isNaN(price) || price <= 0) { toast.error("Enter an offer price greater than 0"); return; }
    const cleaning = form.cleaning_fee_enabled ? (parseFloat(form.cleaning_fee) || 0) : 0;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/offers", {
        guest_name: form.guest_name.trim(), guest_email: form.guest_email.trim(), guest_phone: form.guest_phone || "",
        check_in: form.check_in, check_out: form.check_out, guests: Number(form.guests) || 1,
        total_price: price, cleaning_fee_enabled: !!form.cleaning_fee_enabled, cleaning_fee: cleaning,
        damage_coverage: form.damage_coverage || "none",
        damage_waiver_amount: 59, damage_deposit_amount: 1000,
        expires_in_days: Number(form.expires_in_days) || 7, note: form.note || "",
        inquiry_id: form.inquiry_id || null,
      });
      toast.success(data.emailed ? "Offer created & emailed to guest" : "Offer created — copy the link to share");
      setOpen(false);
      onCreated && onCreated(data);
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="offer-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Send a special offer</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Guest name</Label><Input value={form.guest_name} onChange={(e) => set("guest_name", e.target.value)} className="rounded-lg" data-testid="offer-name" /></div>
            <div><Label>Guest email</Label><Input value={form.guest_email} onChange={(e) => set("guest_email", e.target.value)} className="rounded-lg" data-testid="offer-email" /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label>Check-in</Label><Input type="date" value={form.check_in} onChange={(e) => set("check_in", e.target.value)} className="rounded-lg" data-testid="offer-checkin" /></div>
            <div><Label>Check-out</Label><Input type="date" value={form.check_out} onChange={(e) => set("check_out", e.target.value)} className="rounded-lg" data-testid="offer-checkout" /></div>
            <div><Label>Guests</Label><Input type="number" value={form.guests} onChange={(e) => set("guests", e.target.value)} className="rounded-lg" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Offer price (stay only)</Label><Input type="number" value={form.total_price} onChange={(e) => set("total_price", e.target.value)} placeholder="1200" className="rounded-lg" data-testid="offer-price" /></div>
            <div><Label>Offer expires in (days)</Label><Input type="number" value={form.expires_in_days} onChange={(e) => set("expires_in_days", e.target.value)} className="rounded-lg" data-testid="offer-expiry" /></div>
          </div>

          <div className="rounded-xl border border-border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="cursor-pointer">Cleaning fee</Label>
                <p className="text-xs text-muted-foreground">Added to the offer price. Tax is calculated on the total.</p>
              </div>
              <Switch checked={form.cleaning_fee_enabled} onCheckedChange={(v) => set("cleaning_fee_enabled", v)} data-testid="offer-cleaning-toggle" />
            </div>
            {form.cleaning_fee_enabled && (
              <div><Label>Cleaning fee amount ($)</Label><Input type="number" value={form.cleaning_fee} onChange={(e) => set("cleaning_fee", e.target.value)} placeholder="150" className="rounded-lg" data-testid="offer-cleaning-amount" /></div>
            )}
          </div>

          <div>
            <Label>Damage coverage</Label>
            <Select value={form.damage_coverage} onValueChange={(v) => set("damage_coverage", v)}>
              <SelectTrigger className="rounded-lg" data-testid="offer-damage-coverage"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="waiver">$59 non-refundable damage waiver</SelectItem>
                <SelectItem value="deposit">$1,000 refundable security deposit</SelectItem>
                <SelectItem value="none">None</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div><Label>Note to guest (optional)</Label><Textarea value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Looking forward to hosting you!" className="rounded-lg" /></div>
          <p className="text-xs text-muted-foreground">The guest pays a 50% deposit (plus the non-refundable waiver, if selected) when they accept. Lodging tax and any refundable deposit are collected with the balance. The full breakdown is included in the offer email.</p>
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving} className="rounded-full bg-primary w-full" data-testid="offer-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Create & send offer</>}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Offers() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/offers").then((r) => setItems(r.data)).catch(err);
  useEffect(() => { load(); }, []);
  const del = async (id) => { try { await api.delete(`/admin/offers/${id}`); load(); } catch (e) { err(e); } };
  const copyLink = (token) => { navigator.clipboard.writeText(`${window.location.origin}/offer/${token}`); toast.success("Offer link copied"); };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-semibold text-foreground">Special offers</h1>
        <p className="text-sm text-muted-foreground mt-1">Send custom prices, early check-in, and late-checkout / extend-stay offers to guests.</p>
      </div>
      <Tabs defaultValue="custom">
        <TabsList className="mb-5">
          <TabsTrigger value="custom" data-testid="offers-tab-custom">Custom offers</TabsTrigger>
          <TabsTrigger value="checkin" data-testid="offers-tab-checkin">Check-in &amp; Checkout</TabsTrigger>
        </TabsList>
        <TabsContent value="custom">
          <div className="flex justify-end mb-4">
            <OfferDialog onCreated={load} trigger={<Button className="rounded-full bg-primary" data-testid="new-offer-btn"><Plus className="h-4 w-4 mr-1" /> New offer</Button>} />
          </div>
          <div className="space-y-3">
            {items.length === 0 && <p className="text-sm text-muted-foreground">No offers sent yet.</p>}
            {items.map((o) => (
              <Card key={o.id} data-testid={`offer-${o.id}`}>
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground">{o.guest_name}</span>
                      <Badge className={`${OFFER_STATUS_COLORS[o.status] || ""} border-0 capitalize`}>{o.status}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">{o.guest_email}</div>
                    <div className="text-sm text-foreground mt-2 flex items-center gap-2 flex-wrap">
                      <CalendarDays className="h-4 w-4 text-primary" /> {o.check_in} → {o.check_out} · {o.nights} night(s) · {o.guests} guests
                    </div>
                    <div className="text-sm mt-1"><span className="font-semibold text-primary text-lg">{money(o.grand_total ?? o.total_price)}</span> <span className="text-muted-foreground">total</span> · <span className="text-xs text-muted-foreground"><Clock className="h-3 w-3 inline mb-0.5" /> expires {(o.expires_at || "").slice(0, 10)}</span></div>
                    {o.note && <p className="text-sm text-muted-foreground mt-1 italic">"{o.note}"</p>}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {o.status === "sent" && <Button size="sm" variant="outline" onClick={() => copyLink(o.token)} data-testid={`offer-copy-${o.id}`}><Copy className="h-3.5 w-3.5 mr-1" /> Copy link</Button>}
                    <Button size="sm" variant="ghost" onClick={() => del(o.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="checkin">
          <CheckinCheckoutOffers />
        </TabsContent>
      </Tabs>
    </div>
  );
}


/* ---------------- Check-in & Checkout add-on offers ---------------- */
function CheckinCheckoutOffers() {
  const [cfg, setCfg] = useState(null);
  const [stays, setStays] = useState([]);
  const [savingCfg, setSavingCfg] = useState(false);
  const [early, setEarly] = useState(null); // booking for early-checkin dialog
  const [earlyFee, setEarlyFee] = useState(true);
  const [late, setLate] = useState(null); // booking for late-checkout dialog
  const [avail, setAvail] = useState({ available_nights: 0, options: [] });
  const [lateNights, setLateNights] = useState(0);
  const [lateInclude, setLateInclude] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/addons-config").then((r) => setCfg(r.data)).catch(err);
    api.get("/admin/upcoming-stays").then((r) => setStays(r.data)).catch(err);
  };
  useEffect(() => { load(); }, []);

  const setC = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const { data } = await api.put("/admin/addons-config", {
        early_checkin_fee: Number(cfg.early_checkin_fee), early_checkin_time: cfg.early_checkin_time,
        late_checkout_fee: Number(cfg.late_checkout_fee), late_checkout_time: cfg.late_checkout_time,
      });
      setCfg(data); toast.success("Saved");
    } catch (e) { err(e); } finally { setSavingCfg(false); }
  };

  const openLate = async (b) => {
    setLate(b); setLateInclude(true); setLateNights(0); setAvail({ available_nights: 0, options: [] });
    try {
      const { data } = await api.get(`/admin/bookings/${b.id}/extension-availability?nights=7`);
      setAvail(data); setLateNights(data.available_nights);
    } catch (e) { err(e); }
  };

  const sendEarly = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${early.id}/early-checkin-offer`, { origin_url: window.location.origin, with_fee: earlyFee });
      toast.success(data.with_fee ? "Early check-in offer texted to guest" : "Early check-in message texted to guest");
      setEarly(null);
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const sendLate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${late.id}/late-checkout-offer`, {
        origin_url: window.location.origin, max_extend_nights: Number(lateNights), include_late_checkout: lateInclude,
      });
      toast.success(`Late-checkout offer texted (${data.available_extend_nights} extra night(s) offered)`);
      setLate(null);
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  if (!cfg) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;

  return (
    <div className="space-y-5">
      <Card data-testid="addon-config-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-foreground">Fees &amp; times</h2>
          <Button size="sm" className="rounded-full bg-primary" onClick={saveCfg} disabled={savingCfg} data-testid="addon-config-save">{savingCfg ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Early check-in fee ($)</Label><Input type="number" value={cfg.early_checkin_fee} onChange={(e) => setC("early_checkin_fee", e.target.value)} className="rounded-lg mt-1" data-testid="cfg-early-fee" /></div>
          <div><Label>Early check-in time</Label><Input value={cfg.early_checkin_time} onChange={(e) => setC("early_checkin_time", e.target.value)} placeholder="12:00 PM" className="rounded-lg mt-1" data-testid="cfg-early-time" /></div>
          <div><Label>Late checkout fee ($)</Label><Input type="number" value={cfg.late_checkout_fee} onChange={(e) => setC("late_checkout_fee", e.target.value)} className="rounded-lg mt-1" data-testid="cfg-late-fee" /></div>
          <div><Label>Late checkout time</Label><Input value={cfg.late_checkout_time} onChange={(e) => setC("late_checkout_time", e.target.value)} placeholder="1:00 PM" className="rounded-lg mt-1" data-testid="cfg-late-time" /></div>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Extra-night prices for extensions use your nightly rate (and any seasonal rates) automatically.</p>
      </Card>

      <div>
        <h2 className="font-semibold text-foreground mb-1">Upcoming stays</h2>
        <p className="text-xs text-muted-foreground mb-3">Send these manually. You'll also get a text reminder 2 days before arrival (early check-in) and the day before checkout (late checkout).</p>
        <div className="space-y-3">
          {stays.length === 0 && <p className="text-sm text-muted-foreground">No upcoming confirmed stays.</p>}
          {stays.map((b) => (
            <Card key={b.id} data-testid={`stay-${b.id}`}>
              <div className="flex justify-between items-start gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium text-foreground">{b.guest_name}</div>
                  <div className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap"><CalendarDays className="h-4 w-4 text-primary" /> {b.check_in} → {b.check_out} · {b.guests} guests</div>
                  {!b.guest_phone && <div className="text-xs text-destructive mt-1">No phone on file — can't text this guest.</div>}
                  <div className="flex gap-1 mt-1">
                    {b.early_checkin?.paid && <Badge className="bg-secondary/20 text-secondary border-0">Early check-in paid</Badge>}
                    {b.late_checkout?.paid && <Badge className="bg-secondary/20 text-secondary border-0">Late checkout paid</Badge>}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0 flex-wrap">
                  <Button size="sm" variant="outline" className="rounded-full" disabled={!b.guest_phone} onClick={() => { setEarly(b); setEarlyFee(true); }} data-testid={`send-early-${b.id}`}><Sunrise className="h-4 w-4 mr-1" /> Early check-in</Button>
                  <Button size="sm" variant="outline" className="rounded-full" disabled={!b.guest_phone} onClick={() => openLate(b)} data-testid={`send-late-${b.id}`}><Sunset className="h-4 w-4 mr-1" /> Late checkout</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Early check-in dialog */}
      <Dialog open={!!early} onOpenChange={(o) => !o && setEarly(null)}>
        <DialogContent data-testid="early-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Early check-in offer</DialogTitle>
            <DialogDescription>Text {early?.guest_name} the option to check in early (from {cfg.early_checkin_time}).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <button type="button" onClick={() => setEarlyFee(true)} data-testid="early-with-fee" className={`w-full flex items-center gap-3 rounded-lg border px-3 py-3 text-left transition-all ${earlyFee ? "border-primary bg-primary/5" : "border-border"}`}>
              <span className={`h-4 w-4 rounded-full flex items-center justify-center border ${earlyFee ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>{earlyFee && <span className="h-1.5 w-1.5 rounded-full bg-white" />}</span>
              <span className="flex-1"><span className="block text-sm font-medium text-foreground">Charge a fee — {money(cfg.early_checkin_fee)}</span><span className="block text-xs text-muted-foreground">Guest gets a secure link to pay.</span></span>
            </button>
            <button type="button" onClick={() => setEarlyFee(false)} data-testid="early-no-fee" className={`w-full flex items-center gap-3 rounded-lg border px-3 py-3 text-left transition-all ${!earlyFee ? "border-primary bg-primary/5" : "border-border"}`}>
              <span className={`h-4 w-4 rounded-full flex items-center justify-center border ${!earlyFee ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>{!earlyFee && <span className="h-1.5 w-1.5 rounded-full bg-white" />}</span>
              <span className="flex-1"><span className="block text-sm font-medium text-foreground">Free / courtesy message</span><span className="block text-xs text-muted-foreground">Just let them know they can arrive early — no payment.</span></span>
            </button>
          </div>
          <DialogFooter><Button className="rounded-full bg-primary w-full" onClick={sendEarly} disabled={busy} data-testid="early-send"><Send className="h-4 w-4 mr-1" /> {busy ? "Sending…" : "Text the guest"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Late checkout dialog */}
      <Dialog open={!!late} onOpenChange={(o) => !o && setLate(null)}>
        <DialogContent data-testid="late-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Late checkout &amp; extend</DialogTitle>
            <DialogDescription>Text {late?.guest_name} a secure link to pay for late checkout and/or extra nights.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <button type="button" onClick={() => setLateInclude((v) => !v)} data-testid="late-include-toggle" className={`w-full flex items-center gap-3 rounded-lg border px-3 py-3 text-left transition-all ${lateInclude ? "border-primary bg-primary/5" : "border-border"}`}>
              <span className={`h-4 w-4 rounded flex items-center justify-center border ${lateInclude ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>{lateInclude && <Check className="h-3 w-3 text-white" />}</span>
              <span className="flex-1"><span className="block text-sm font-medium text-foreground">Offer late checkout until {cfg.late_checkout_time}</span><span className="block text-xs text-muted-foreground">{money(cfg.late_checkout_fee)}</span></span>
            </button>
            <div>
              <Label>Nights they can extend</Label>
              <div className="flex items-center gap-3 mt-1.5">
                <Button type="button" variant="outline" size="icon" className="h-9 w-9 rounded-full" onClick={() => setLateNights((n) => Math.max(0, n - 1))} data-testid="late-nights-minus"><Minus className="h-4 w-4" /></Button>
                <span className="w-8 text-center font-medium" data-testid="late-nights-count">{lateNights}</span>
                <Button type="button" variant="outline" size="icon" className="h-9 w-9 rounded-full" onClick={() => setLateNights((n) => Math.min(avail.available_nights, n + 1))} data-testid="late-nights-plus"><Plus className="h-4 w-4" /></Button>
                <span className="text-xs text-muted-foreground" data-testid="late-avail-note">{avail.available_nights} night(s) open after checkout</span>
              </div>
              {lateNights > 0 && (
                <div className="mt-2 space-y-1 rounded-lg bg-accent/50 p-2">
                  {avail.options.slice(0, lateNights).map((o) => (
                    <div key={o.date} className="flex justify-between text-xs text-muted-foreground"><span>Night of {o.date}</span><span className="text-foreground">{money(o.price)}</span></div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button className="rounded-full bg-primary w-full" onClick={sendLate} disabled={busy || (!lateInclude && lateNights === 0)} data-testid="late-send"><Send className="h-4 w-4 mr-1" /> {busy ? "Sending…" : "Text the guest"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ---------------- Automated Guest Messages ---------------- */
const MSG_META = [
  { key: "thanks_booking", title: "Thanks for booking", when: "", hint: "A warm thank-you sent automatically the moment a guest books.", immediate: true },
  { key: "pre_arrival", title: "Pre-arrival note", when: "days before check-in", hint: "Directions, door code, check-in time." },
  { key: "check_in_day", title: "Check-in day welcome", when: "days before check-in (0 = day of)", hint: "A warm 'you're all set' message." },
  { key: "balance_reminder", title: "Balance-due reminder", when: "days before check-in", hint: "Only sent if a balance is still owed." },
  { key: "checkout_instructions", title: "Check-out instructions", when: "days before check-out (1 = day before)", hint: "How to leave the home — trash, dishwasher, lock up, check-out time." },
  { key: "post_checkout", title: "Post-checkout review invite", when: "days after check-out", hint: "Thank the guest and invite a review — include {review_link}. Sent by email + text." },
];
const PLACEHOLDERS = "{guest_name} {property_name} {check_in} {check_out} {nights} {guests} {door_code} {arrival_info} {check_in_time} {check_out_time} {balance_due} {review_link}";

export function Messaging() {
  const [prop, setProp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [sendingNow, setSendingNow] = useState(null);
  const [qrLinks, setQrLinks] = useState({});
  const [imgBusy, setImgBusy] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  useEffect(() => { api.get("/property").then((r) => { setProp(r.data); setTestEmail(r.data.contact_email || ""); setTestPhone(r.data.sms_alert_number || ""); }).catch(err); }, []);
  if (!prop) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;

  const m = prop.messaging || {};
  const setM = (k, v) => setProp({ ...prop, messaging: { ...m, [k]: v } });
  const setTpl = (type, k, v) => setProp({ ...prop, messaging: { ...m, [type]: { ...(m[type] || {}), [k]: v } } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/property", { messaging: {
        door_code: m.door_code || "", arrival_info: m.arrival_info || "",
        thanks_booking: m.thanks_booking, pre_arrival: m.pre_arrival, check_in_day: m.check_in_day, balance_reminder: m.balance_reminder, checkout_instructions: m.checkout_instructions, post_checkout: m.post_checkout,
      } });
      toast.success("Messaging settings saved");
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try { const { data } = await api.post("/admin/messaging/run"); toast.success(`Ran now — ${data.sent} message(s) sent`); }
    catch (e) { err(e); } finally { setRunning(false); }
  };

  const sendNow = async (key, title) => {
    setSendingNow(key);
    try {
      const { data } = await api.post("/admin/messaging/send-now", { type: key });
      toast.success(data.sent ? `"${title}" sent to ${data.sent} guest(s) now` : `No guests are due for "${title}" today`);
    } catch (e) { err(e); } finally { setSendingNow(null); }
  };

  const genQr = async (key) => {
    const link = (qrLinks[key] || "").trim();
    if (!link) { toast.error("Enter a link for the QR code first"); return; }
    setImgBusy(key);
    try {
      const { data } = await api.post("/admin/messaging/generate-qr", { link, origin: window.location.origin });
      setTpl(key, "image_url", data.url);
      toast.success("QR code created — Save to keep it");
    } catch (e) { err(e); } finally { setImgBusy(null); }
  };

  const uploadImg = async (key, file) => {
    if (!file) return;
    setImgBusy(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("origin", window.location.origin);
      const { data } = await api.post("/admin/messaging/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTpl(key, "image_url", data.url);
      toast.success("Image added — Save to keep it");
    } catch (e) { err(e); } finally { setImgBusy(null); }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter an email address to send the test to"); return; }
    setTestBusy(true);
    try {
      const { data } = await api.post("/admin/messaging/send-test", { email: testEmail.trim(), phone: testPhone.trim(), origin: window.location.origin });
      const n = (data.sent || []).length;
      const texted = (data.sent || []).some((s) => s.texted);
      toast.success(`Sent ${n} test message(s) to ${testEmail}${testPhone.trim() && texted ? " + " + testPhone : ""}. Check your inbox${testPhone.trim() ? " and phone" : ""}.`);
    } catch (e) { err(e); } finally { setTestBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-foreground">Guest Messages</h1>
          <p className="text-sm text-muted-foreground mt-1">Automated emails + texts sent around each confirmed stay, at the time of day you choose. Texts go only to guests who opted in to SMS.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={runNow} disabled={running} data-testid="messaging-run-now">{running ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1" /> Run now</>}</Button>
          <Button className="rounded-full bg-primary" onClick={save} disabled={saving} data-testid="messaging-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </div>
      </div>

      <div className="flex items-center gap-2 rounded-lg bg-accent/60 px-3 py-2 text-xs text-muted-foreground mb-6">
        <AlertCircle className="h-3.5 w-3.5 shrink-0 text-primary" /> Emails send only when RESEND_API_KEY is configured. Available placeholders: <span className="font-mono">{PLACEHOLDERS}</span>
      </div>

      <Card className="mb-5 border-primary/30 bg-primary/5">
        <div className="flex items-center gap-2 mb-1">
          <Send className="h-4 w-4 text-primary" />
          <h2 className="font-semibold text-foreground">Send a test to myself</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-3">Fires all six messages (as email and, if you add a phone, as text) to you — using your saved wording, QR codes and links. Great for checking exactly what guests will see. <strong>Tip:</strong> use this on your live site after publishing to see the production version.</p>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1">
            <Label className="text-xs">Your email</Label>
            <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@email.com" className="rounded-lg mt-1" data-testid="msg-test-email" />
          </div>
          <div className="flex-1">
            <Label className="text-xs">Your mobile (optional, for texts)</Label>
            <Input type="tel" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+16025551234" className="rounded-lg mt-1" data-testid="msg-test-phone" />
          </div>
          <Button className="rounded-full bg-primary shrink-0" onClick={sendTest} disabled={testBusy} data-testid="msg-send-test">{testBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Send test set</>}</Button>
        </div>
      </Card>

      <Card className="mb-5">
        <h2 className="font-semibold text-foreground mb-1">Arrival details</h2>
        <p className="text-sm text-muted-foreground mb-4">Inserted into messages via <span className="font-mono">{"{door_code}"}</span> and <span className="font-mono">{"{arrival_info}"}</span>.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Door / lock code</Label><Input value={m.door_code || ""} onChange={(e) => setM("door_code", e.target.value)} placeholder="1234" className="rounded-lg mt-1" data-testid="msg-door-code" /></div>
          <div className="sm:col-span-2"><Label>Directions & parking notes</Label><Textarea value={m.arrival_info || ""} onChange={(e) => setM("arrival_info", e.target.value)} placeholder="Park in the driveway. The lockbox is on the front door…" className="rounded-lg mt-1" data-testid="msg-arrival-info" /></div>
        </div>
      </Card>

      <div className="space-y-4">
        {MSG_META.map(({ key, title, when, hint, immediate }) => {
          const t = m[key] || {};
          return (
            <Card key={key} data-testid={`msg-card-${key}`}>
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-semibold text-foreground">{title}</h2>
                <Switch checked={!!t.enabled} onCheckedChange={(v) => setTpl(key, "enabled", v)} data-testid={`msg-enabled-${key}`} />
              </div>
              <p className="text-xs text-muted-foreground mb-3">{hint}</p>
              <div className={`space-y-3 ${t.enabled ? "" : "opacity-50 pointer-events-none"}`}>
                {immediate ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground bg-accent/50 rounded-lg px-3 py-2">
                    <Clock className="h-4 w-4 text-primary shrink-0" /> Sent automatically right after a guest completes their booking.
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Label className="text-sm">Send</Label>
                    <Input type="number" value={t.days ?? 0} onChange={(e) => setTpl(key, "days", Number(e.target.value))} className="rounded-lg w-20 h-9" data-testid={`msg-days-${key}`} />
                    <span className="text-sm text-muted-foreground">{when}</span>
                    <span className="text-sm text-muted-foreground">at</span>
                    <Input type="time" value={t.time || "09:00"} onChange={(e) => setTpl(key, "time", e.target.value)} className="rounded-lg w-32 h-9" data-testid={`msg-time-${key}`} />
                  </div>
                )}
                <div><Label className="text-xs">Subject</Label><Input value={t.subject || ""} onChange={(e) => setTpl(key, "subject", e.target.value)} className="rounded-lg mt-1" data-testid={`msg-subject-${key}`} /></div>
                <div><Label className="text-xs">Email message</Label><Textarea value={t.body || ""} onChange={(e) => setTpl(key, "body", e.target.value)} rows={6} className="rounded-lg mt-1" data-testid={`msg-body-${key}`} /></div>
                <div>
                  <Label className="text-xs flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5 text-secondary" /> Text message (SMS)</Label>
                  <Textarea value={t.sms || ""} onChange={(e) => setTpl(key, "sms", e.target.value)} rows={3} className="rounded-lg mt-1" placeholder="Short text version — sent only to guests who opted in to SMS. Leave blank to skip texting." data-testid={`msg-sms-${key}`} />
                  <p className="text-[11px] text-muted-foreground mt-1">Sent by text only to guests who opted in. Keep it short. Same placeholders work.</p>
                </div>
                <div className="rounded-lg border border-border bg-accent/20 p-3">
                  <Label className="text-xs flex items-center gap-1.5"><ImageIcon className="h-3.5 w-3.5 text-secondary" /> Email image / QR code (optional)</Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">Shown in the email version only. Generate a QR code from a link, or upload your own image. By default it appears at the <strong>bottom</strong> of the email — to place it exactly where you want, type <span className="font-mono">{"{image}"}</span> in the Email message above.</p>
                  {t.image_url ? (
                    <div className="flex items-center gap-3 mb-2">
                      <img src={t.image_url} alt="Message attachment" className="h-20 w-20 object-contain rounded border border-border bg-white" data-testid={`msg-image-preview-${key}`} />
                      <Button size="sm" variant="outline" className="rounded-full text-xs text-destructive" onClick={() => setTpl(key, "image_url", "")} data-testid={`msg-image-remove-${key}`}><X className="h-3.5 w-3.5 mr-1" /> Remove</Button>
                    </div>
                  ) : null}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex gap-2 flex-1">
                      <Input value={qrLinks[key] || ""} onChange={(e) => setQrLinks({ ...qrLinks, [key]: e.target.value })} placeholder="https://guidebook.myazbnb.com" className="rounded-lg h-9 text-sm" data-testid={`msg-qr-link-${key}`} />
                      <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => genQr(key)} disabled={imgBusy === key} data-testid={`msg-qr-generate-${key}`}>{imgBusy === key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Make QR"}</Button>
                    </div>
                    <label className="inline-flex items-center justify-center gap-1.5 rounded-full border border-input h-9 px-4 text-sm cursor-pointer hover:bg-accent shrink-0" data-testid={`msg-image-upload-label-${key}`}>
                      <Upload className="h-3.5 w-3.5" /> Upload
                      <input type="file" accept="image/*" className="hidden" data-testid={`msg-image-upload-${key}`} onChange={(e) => { uploadImg(key, e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 pt-1 border-t border-border">
                  <p className="text-[11px] text-muted-foreground">Save your changes first, then use Send now to deliver this message immediately to today's matching guests.</p>
                  <Button size="sm" variant="outline" className="rounded-full text-xs shrink-0" onClick={() => sendNow(key, title)} disabled={sendingNow === key} data-testid={`msg-send-now-${key}`}>{sendingNow === key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Send now</>}</Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}



/* ---------------- Guest Reminders (weekly during-stay SMS) ---------------- */
const WEEKDAYS = [
  { v: 0, label: "Monday" }, { v: 1, label: "Tuesday" }, { v: 2, label: "Wednesday" },
  { v: 3, label: "Thursday" }, { v: 4, label: "Friday" }, { v: 5, label: "Saturday" }, { v: 6, label: "Sunday" },
];
const US_TIMEZONES = [
  { v: "America/Phoenix", label: "Arizona — MST (no DST)" },
  { v: "America/Los_Angeles", label: "Pacific — PT" },
  { v: "America/Denver", label: "Mountain — MT" },
  { v: "America/Chicago", label: "Central — CT" },
  { v: "America/New_York", label: "Eastern — ET" },
];
const fmtTime = (t) => {
  const [h, m] = String(t || "09:00").split(":").map(Number);
  const am = h < 12;
  return `${h % 12 || 12}:${String(m || 0).padStart(2, "0")} ${am ? "AM" : "PM"}`;
};

export function Reminders() {
  const blank = { label: "", message: "", weekday: 6, time: "18:00", active: true };
  const [items, setItems] = useState([]);
  const [tz, setTz] = useState("America/Phoenix");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);

  const load = () => {
    api.get("/admin/reminders").then((r) => setItems(r.data)).catch(err);
    api.get("/property").then((r) => setTz(r.data.timezone || "America/Phoenix")).catch(err);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ label: it.label || "", message: it.message, weekday: it.weekday, time: it.time, active: it.active }); setOpen(true); };

  const save = async () => {
    if (!form.message.trim()) { toast.error("Enter a message"); return; }
    setSaving(true);
    const payload = { label: form.label.trim(), message: form.message.trim(), weekday: Number(form.weekday), time: form.time, active: !!form.active };
    try {
      if (editing) await api.put(`/admin/reminders/${editing.id}`, payload);
      else await api.post("/admin/reminders", payload);
      toast.success("Reminder saved");
      setOpen(false); load();
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  const del = async (id) => { try { await api.delete(`/admin/reminders/${id}`); toast.success("Deleted"); load(); } catch (e) { err(e); } };
  const toggle = async (it) => { try { await api.put(`/admin/reminders/${it.id}`, { label: it.label, message: it.message, weekday: it.weekday, time: it.time, active: !it.active }); load(); } catch (e) { err(e); } };
  const sendTest = async (it) => { setTestingId(it.id); try { const { data } = await api.post(`/admin/reminders/${it.id}/test`); toast.success(`Test text sent to ${data.to}`); } catch (e) { err(e); } finally { setTestingId(null); } };
  const saveTz = async (v) => { const prev = tz; setTz(v); try { await api.put("/admin/property", { timezone: v }); toast.success("Timezone updated"); } catch (e) { setTz(prev); err(e); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-foreground">Guest Reminders</h1>
          <p className="text-sm text-muted-foreground mt-1">Weekly text reminders sent automatically to guests <strong>during their stay</strong> (e.g. "Roll the trash can to the curb tonight"). Skips arrival &amp; departure days.</p>
        </div>
        <Button className="rounded-full bg-primary" onClick={openNew} data-testid="add-reminder-btn"><Plus className="h-4 w-4 mr-1" /> New reminder</Button>
      </div>

      <div className="flex items-center gap-2 rounded-lg bg-accent/60 px-3 py-2 text-xs text-muted-foreground mb-5">
        <AlertCircle className="h-3.5 w-3.5 shrink-0 text-primary" /> Texts send only to guests who left a phone number, and require Twilio to be configured. Times use the timezone below.
      </div>

      <Card className="mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <Clock className="h-4 w-4 text-primary shrink-0" />
          <Label className="text-sm">Property timezone</Label>
          <Select value={tz} onValueChange={saveTz}>
            <SelectTrigger className="w-64 rounded-lg h-9" data-testid="reminder-timezone"><SelectValue /></SelectTrigger>
            <SelectContent>
              {US_TIMEZONES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">All reminder times are interpreted in this timezone.</span>
        </div>
      </Card>

      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No reminders yet. Create one to text guests during their stay.</p>}
        {items.map((it) => (
          <Card key={it.id} data-testid={`reminder-row-${it.id}`} className={`flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 ${it.active ? "" : "opacity-60"}`}>
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <span className="h-10 w-10 shrink-0 rounded-full bg-secondary/20 flex items-center justify-center mt-0.5"><BellRing className="h-5 w-5 text-secondary" /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-primary/15 text-primary border-0">{WEEKDAYS[it.weekday]?.label}</Badge>
                  <Badge variant="secondary" className="font-medium">{fmtTime(it.time)}</Badge>
                  {it.label && <span className="text-sm font-medium text-foreground">{it.label}</span>}
                </div>
                <p className="text-sm text-muted-foreground mt-1.5 whitespace-pre-line break-words">{it.message}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0 self-end sm:self-start">
              <Switch checked={it.active} onCheckedChange={() => toggle(it)} data-testid={`reminder-active-${it.id}`} />
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => sendTest(it)} disabled={testingId === it.id} title="Send a test to your alert number" data-testid={`reminder-test-${it.id}`}>{testingId === it.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 text-secondary" />}</Button>
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => openEdit(it)} data-testid={`reminder-edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => del(it.id)} data-testid={`reminder-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="reminder-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit reminder" : "New reminder"}</DialogTitle>
            <DialogDescription>Sent by text to guests during their stay, on the day and time you choose.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div><Label>Label (optional, for you)</Label><Input value={form.label} onChange={(e) => set("label", e.target.value)} placeholder="Trash day" className="rounded-lg mt-1" data-testid="reminder-form-label" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Day of week</Label>
                <Select value={String(form.weekday)} onValueChange={(v) => set("weekday", Number(v))}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="reminder-form-weekday"><SelectValue /></SelectTrigger>
                  <SelectContent>{WEEKDAYS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Time</Label><Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className="rounded-lg mt-1" data-testid="reminder-form-time" /></div>
            </div>
            <div><Label>Message</Label><Textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={4} placeholder="Please roll the trash can to the curb tonight — pickup is tomorrow morning. Thank you!" className="rounded-lg mt-1" data-testid="reminder-form-message" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="reminder-form-active" /><Label className="text-sm">Active</Label></div>
          </div>
          <DialogFooter>
            <Button className="rounded-full bg-primary w-full" onClick={save} disabled={saving} data-testid="reminder-form-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save reminder"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



/* ---------------- Affiliates (referral commissions) ---------------- */
const AFF_BLANK = { name: "", email: "", code: "", commission_type: "percent", commission_value: 5, active: true };

function AffiliateForm({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(AFF_BLANK);
  const [saving, setSaving] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) setForm(editing ? { name: editing.name, email: editing.email || "", code: editing.code, commission_type: editing.commission_type, commission_value: editing.commission_value, active: editing.active } : AFF_BLANK);
  }, [open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Enter the affiliate's name"); return; }
    if (!form.code.trim()) { toast.error("Enter a referral code"); return; }
    const val = Number(form.commission_value);
    if (Number.isNaN(val) || val < 0) { toast.error("Enter a valid commission amount"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), email: form.email.trim(), code: form.code.trim().toUpperCase(), commission_type: form.commission_type, commission_value: val, active: !!form.active, origin_url: window.location.origin };
      if (editing) await api.put(`/admin/affiliates/${editing.id}`, payload);
      else await api.post("/admin/affiliates", payload);
      toast.success(editing ? "Affiliate updated" : "Affiliate created");
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="affiliate-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit affiliate" : "New affiliate"}</DialogTitle>
          <DialogDescription>Create a referral code and set the commission you'll pay on completed stays.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith" className="rounded-lg mt-1" data-testid="affiliate-name" /></div>
            <div><Label>Email (optional)</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@example.com" className="rounded-lg mt-1" data-testid="affiliate-email" /></div>
          </div>
          <div>
            <Label>Referral code</Label>
            <Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase().replace(/\s/g, ""))} placeholder="JANE" className="rounded-lg mt-1 uppercase font-mono" data-testid="affiliate-code" />
            <p className="text-xs text-muted-foreground mt-1">Their link will be <span className="font-mono">{window.location.origin}/?ref={form.code || "CODE"}</span></p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Commission type</Label>
              <Select value={form.commission_type} onValueChange={(v) => set("commission_type", v)}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="affiliate-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percent of total (%)</SelectItem>
                  <SelectItem value="flat">Flat per booking ($)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{form.commission_type === "percent" ? "Percentage (%)" : "Amount ($)"}</Label><Input type="number" value={form.commission_value} onChange={(e) => set("commission_value", e.target.value)} className="rounded-lg mt-1" data-testid="affiliate-value" /></div>
          </div>
          <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="affiliate-active" /><Label className="text-sm">Active (link works &amp; earns commission)</Label></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="rounded-full bg-primary w-full" data-testid="affiliate-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Save changes" : "Create affiliate")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AffiliateBookings({ id }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/admin/affiliates/${id}/bookings`).then((r) => setRows(r.data)).catch(err); }, [id]);
  if (rows === null) return <div className="py-3 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading bookings…</div>;
  if (rows.length === 0) return <p className="py-3 text-sm text-muted-foreground">No bookings attributed yet.</p>;
  return (
    <div className="mt-3 rounded-lg border border-border overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Guest</TableHead><TableHead>Dates</TableHead><TableHead>Total</TableHead><TableHead>Commission</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((b) => (
            <TableRow key={b.id} data-testid={`affiliate-booking-${b.id}`}>
              <TableCell className="font-mono">{b.booking_number || "—"}</TableCell>
              <TableCell>{b.guest_name}</TableCell>
              <TableCell className="text-xs whitespace-nowrap">{b.check_in} → {b.check_out}</TableCell>
              <TableCell>{money(b.total)}</TableCell>
              <TableCell className="font-semibold text-primary">{money(b.commission)}</TableCell>
              <TableCell>
                {b.paid ? <Badge className="bg-secondary/20 text-secondary border-0">Paid</Badge>
                  : b.earned ? <Badge className="bg-amber-500/15 text-amber-600 border-0">Owed</Badge>
                  : b.status === "confirmed" ? <Badge variant="secondary">Stay upcoming</Badge>
                  : <Badge variant="secondary" className="capitalize">{b.status.replace("_", " ")}</Badge>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function Affiliates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [signup, setSignup] = useState(null);
  const load = () => api.get("/admin/affiliates").then((r) => setItems(r.data)).catch(err);
  useEffect(() => {
    load();
    api.get(`/admin/affiliates/signup-info?origin=${encodeURIComponent(window.location.origin)}`).then((r) => setSignup(r.data)).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setOpen(true); };
  const del = async (a) => { if (!window.confirm(`Delete affiliate "${a.name}"? Their booking history stays but stops tracking.`)) return; try { await api.delete(`/admin/affiliates/${a.id}`); toast.success("Deleted"); load(); } catch (e) { err(e); } };
  const copyLink = (code) => { navigator.clipboard?.writeText(`${window.location.origin}/?ref=${code}`); toast.success("Referral link copied"); };
  const copySignup = () => { navigator.clipboard?.writeText(signup?.signup_url || ""); toast.success("Signup link copied"); };
  const downloadQr = async () => {
    try {
      const res = await fetch(signup.qr_url); const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "myazbnb-affiliate-signup-qr.png"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { window.open(signup.qr_url, "_blank"); }
  };
  const approve = async (a) => {
    setApprovingId(a.id);
    try { await api.post(`/admin/affiliates/${a.id}/approve`); toast.success(`${a.name} approved — welcome email & text sent`); load(); }
    catch (e) { err(e); } finally { setApprovingId(null); }
  };
  const declineApp = async (a) => {
    if (!window.confirm(`Decline ${a.name}'s application? They'll be emailed and texted the result.`)) return;
    try { await api.post(`/admin/affiliates/${a.id}/decline`); toast.success("Application declined — applicant notified"); load(); }
    catch (e) { err(e); }
  };
  const markPaid = async (a) => {
    setPayingId(a.id);
    try { const { data } = await api.post(`/admin/affiliates/${a.id}/mark-paid`); toast.success(data.marked_paid ? `Marked ${data.marked_paid} booking(s) paid` : "Nothing outstanding to pay"); load(); }
    catch (e) { err(e); } finally { setPayingId(null); }
  };
  const commissionLabel = (a) => a.commission_type === "percent" ? `${a.commission_value}% of total` : `${money(a.commission_value)} per booking`;

  const pending = items.filter((a) => a.status === "pending");
  const activeList = items.filter((a) => a.status !== "pending");

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-foreground">Affiliates</h1>
          <p className="text-sm text-muted-foreground mt-1">Give partners a referral link. You pay commission only after a referred guest's stay is completed.</p>
        </div>
        <Button className="rounded-full bg-primary" onClick={openNew} data-testid="add-affiliate-btn"><Plus className="h-4 w-4 mr-1" /> New affiliate</Button>
      </div>

      {signup && (
        <Card className="mb-6 bg-accent/40" data-testid="signup-share-card">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
            <img src={signup.qr_url} alt="Affiliate signup QR" className="h-28 w-28 rounded-xl bg-white p-2 shrink-0 self-center sm:self-auto" data-testid="signup-qr-img" />
            <div className="flex-1 min-w-0">
              <h2 className="font-serif text-lg text-foreground">Recruit new affiliates</h2>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">Share this link or QR code. People sign up, then appear below for your approval.</p>
              <div className="flex items-center gap-2 bg-white rounded-lg border border-border px-3 py-2 text-xs font-mono break-all mb-2" data-testid="signup-url">{signup.signup_url}</div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={copySignup} data-testid="copy-signup-btn"><Copy className="h-3.5 w-3.5 mr-1" /> Copy link</Button>
                <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={downloadQr} data-testid="download-signup-qr-btn"><Download className="h-3.5 w-3.5 mr-1" />Download QR</Button>
                <a href={signup.signup_url} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="rounded-full text-xs" data-testid="preview-signup-btn">Preview page</Button></a>
              </div>
            </div>
          </div>
        </Card>
      )}

      {pending.length > 0 && (
        <div className="mb-6">
          <h2 className="font-serif text-lg text-foreground mb-3 flex items-center gap-2">Pending applications <Badge className="bg-amber-500/15 text-amber-600 border-0">{pending.length}</Badge></h2>
          <div className="space-y-3">
            {pending.map((a) => (
              <Card key={a.id} data-testid={`pending-affiliate-${a.id}`} className="border-amber-300/60">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground">{a.name}</span>
                      <Badge className="bg-primary/15 text-primary border-0 font-mono">{a.code}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{a.email}{a.phone ? ` · ${a.phone}` : ""} · {commissionLabel(a)}</div>
                    {a.note && <p className="text-sm text-muted-foreground mt-2 italic">“{a.note}”</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 self-end sm:self-start">
                    <Button size="sm" className="rounded-full bg-secondary text-xs" disabled={approvingId === a.id} onClick={() => approve(a)} data-testid={`approve-affiliate-${a.id}`}>{approvingId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve</>}</Button>
                    <Button size="sm" variant="ghost" className="rounded-full text-xs text-destructive" onClick={() => del(a)} data-testid={`decline-affiliate-${a.id}`}>Decline</Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 rounded-lg bg-accent/60 px-3 py-2 text-xs text-muted-foreground mb-6">
        <AlertCircle className="h-3.5 w-3.5 shrink-0 text-primary" /> Share <span className="font-mono">yoursite.com/?ref=CODE</span>. Bookings within 30 days of the click are credited. Commission becomes "owed" once the stay checks out.
      </div>

      <div className="space-y-4">
        {activeList.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No affiliates yet. Create one or share your signup link above.</p>}
        {activeList.map((a) => {
          const s = a.stats || {};
          return (
            <Card key={a.id} data-testid={`affiliate-${a.id}`} className={a.active ? "" : "opacity-70"}>
              <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <span className="h-10 w-10 shrink-0 rounded-full bg-primary/10 flex items-center justify-center mt-0.5"><Users className="h-5 w-5 text-primary" /></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground">{a.name}</span>
                      <Badge className="bg-primary/15 text-primary border-0 font-mono">{a.code}</Badge>
                      {!a.active && <Badge variant="secondary">Off</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{a.email ? `${a.email} · ` : ""}{commissionLabel(a)}</div>
                    <button onClick={() => copyLink(a.code)} data-testid={`affiliate-copy-${a.id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary hover:underline break-all"><Copy className="h-3.5 w-3.5 shrink-0" /> {window.location.origin}/?ref={a.code}</button>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 self-end sm:self-start">
                  <Button size="sm" variant="outline" className="rounded-full text-xs" disabled={payingId === a.id || (s.outstanding || 0) <= 0} onClick={() => markPaid(a)} data-testid={`affiliate-markpaid-${a.id}`}>{payingId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><DollarSign className="h-3.5 w-3.5 mr-1" /> Mark paid</>}</Button>
                  <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => openEdit(a)} data-testid={`affiliate-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive" onClick={() => del(a)} data-testid={`affiliate-delete-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                {[
                  { label: "Clicks", value: s.clicks ?? 0 },
                  { label: "Completed stays", value: s.completed_bookings ?? 0 },
                  { label: "Commission owed", value: money(s.outstanding ?? 0), accent: (s.outstanding ?? 0) > 0 },
                  { label: "Paid to date", value: money(s.paid ?? 0) },
                ].map((m) => (
                  <div key={m.label} className="rounded-lg bg-muted px-3 py-2">
                    <div className="text-[11px] text-muted-foreground">{m.label}</div>
                    <div className={`text-lg font-serif font-semibold ${m.accent ? "text-amber-600" : "text-foreground"}`}>{m.value}</div>
                  </div>
                ))}
              </div>

              <button onClick={() => setExpanded(expanded === a.id ? null : a.id)} data-testid={`affiliate-toggle-${a.id}`} className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded === a.id ? "rotate-180" : ""}`} /> {expanded === a.id ? "Hide" : "View"} attributed bookings
              </button>
              {expanded === a.id && <AffiliateBookings id={a.id} />}
            </Card>
          );
        })}
      </div>

      <AffiliateForm open={open} onOpenChange={setOpen} editing={editing} onSaved={load} />
    </div>
  );
}
